/**
 * The level list, its hero, its world headers and its rows — plus the
 * chapter names and taglines, keyed by the English `world:` string
 * (components/home/Home.tsx, LevelRow.tsx).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * Numbers are formatted through lib/format with an explicit locale rather
 * than the ambient one, because a catalogue is a value: `stringsFor('ar')`
 * has to build Arabic sentences whether or not the page it is being read on
 * is currently Arabic. That is how the tests read it, and it costs one
 * argument.
 *
 * The fake terminal in the hero is *not* here. `SHELL_PROMPT`, the command it
 * types and the two entries of scrollback above it are what Level 0 actually
 * runs in the guest (missions/l00-hello-terminal/seed.sh), so they are
 * machine output in both languages and they live in the component beside the
 * comment that says so.
 */
import type { ReactNode } from 'react';

import { ltr, money, num } from '../format';

export const en = {
  /* Meet Iris (components/home/MeetIris.tsx), rewritten on 2026-10-18 for the
     owner's direction: she is an agent the learner directs, not a tutor they
     chat with. The three steps are the loop every scripted level runs —
     brief her, approve or reject what she proposes, check what she did — and
     each exchange is the learner's line, then hers. No sentence here names how
     many levels she is scripted on; `access` takes that from the catalogue. */
  irisIntro: {
    role: 'Your designed AI agent', meet: 'Meet Iris',
    greeting: 'Hi, I’m Iris. You direct, I type.',
    artAlt: 'Iris, a graphite and ivory robot with one green eye, welcomes you.',
    pause: 'Pause animation', play: 'Play animation',
    title: 'You direct. Iris does the typing.',
    body: 'Iris proposes every command and you decide: approve it, reject it, or tell her what to do instead. She is designed to be wrong sometimes, so the skill you build is judging an agent’s work.',
    principle: 'Read what she proposes, ask why when it is unclear, and never approve a command you could not explain.',
    cta: 'Start building', preview: 'Example exchange', choose: 'Explore directing Iris',
    /** `through` is the last level of the unbroken scripted run from 0 (Landing.tsx, `scriptedThrough`). */
    access: (through: number) => `On levels 0 to ${num(through, 'en')} Iris does the typing; you direct her and decide what she runs.`,
    note: (_agentFrom: number) => `Example exchanges. Iris proposes commands for you to approve from the first level, through the opening chapters and on every checkpoint. Like any agent she is sometimes wrong, so you practise reviewing her work.`,
    examples: [
      {label:'Brief',question:'The tests fail on main. Find out why, but change nothing yet.',answer:'I propose running npm test and reading the first failure. It only reads, so nothing changes. Approve it?',takeaway:'Say the goal and the limits; she proposes the first step.'},
      {label:'Approve or reject',question:'You want to force-push. Why?',answer:'To replace the remote branch with ours. You are right to stop me: it would drop Priya’s commits. I will fetch and rebase instead.',takeaway:'Reject what you would not sign, and say what to do instead.'},
      {label:'Check her work',question:'You say the fix is done. Show me.',answer:'The diff touches one file and npm test passes. I did not add a test for the empty input. Shall I?',takeaway:'Done means evidence: read the diff, run the checks.'},
    ],
  },
  /* The top bar: what you can do with the record you have built. */
  bar: {
    notSynced: 'not synced',
    notSyncedWhy: 'The last change did not reach your account. It will be retried.',
    /* The generator is gone: progress lives on the account now, so nothing
       invites anybody to make a new code. This is the reader, kept for the
       people who took one before that was true. */
    restoreCode: 'Have a save code?',
    restoreCodeWhy: 'Paste a code you saved before, and its levels join this browser',
  },

  /* The pitch, and the one button under it. */
  hero: {
    /* Two lines on purpose, so the break is the translator's to place. */
    title: (br: ReactNode) => ['Work with AI agents.', br, 'Get the job done.'],
    ctaStart: (level: number) => `Start · Level ${num(level, 'en')}`,
    ctaContinue: (level: number) => `Continue · Level ${num(level, 'en')}`,
    /**
     * Two sentences for one button, because the paid half cannot always be
     * named by where it runs.
     *
     * The range is the better sentence whenever there is one: a learner who
     * has just scrolled a ledger of numbered rows reads `20-51` as the rows
     * themselves, which is more than any count can say. But `paidSpan` hands
     * a range over only when every locked level is one lane's tail
     * (lib/entitlement.ts), and a specialization track is priced whole and
     * numbered from zero — so the day one ships, the only range anybody could
     * quote for it is `0-19`, which is a run of numbers this learner already
     * played on the general track.
     *
     * So the second sentence leans on the count instead. The count is true
     * whatever shape the paid content is in; the range is a nicety, and a
     * nicety that is sometimes a lie is not one.
     */
    ctaUnlock: (from: number, to: number) => `Unlock levels ${num(from, 'en')}-${num(to, 'en')}`,
    ctaUnlockCount: (count: number) => `Unlock the remaining ${num(count, 'en')} level${count === 1 ? '' : 's'}`,
    ctaReplay: 'All levels cleared · Replay',
    /* Before the catalogue has arrived there is no level to name. */
    ctaIdle: 'Start',
    noteNext: (level: number, minutes: number) =>
      `Level ${num(level, 'en')} takes about ${num(minutes, 'en')} minutes.`,
    /* The note under the button, and the same pair for the same reason. Both
       halves of it are facts about this browser's catalogue: what it holds,
       and what it does not. */
    noteUnlock: (owned: number, from: number, to: number) =>
      `All ${num(owned, 'en')} levels you own, cleared. Levels ${num(from, 'en')}-${num(to, 'en')} are the rest of the course.`,
    noteUnlockCount: (owned: number, count: number) =>
      `All ${num(owned, 'en')} levels you own, cleared. There ${count === 1 ? 'is' : 'are'} ${num(count, 'en')} more in the full course.`,
    noteReplay: 'Every level cleared. Replay any of them for a better score.',
    noteIdle: 'Levels are loading.',
  },

  /**
   * The offer: the one place in the product that asks for money, and the one
   * place that says what the course costs.
   *
   * It reads as an invitation rather than a wall, which is a decision about
   * where it sits as much as about the words. A learner meets it having just
   * scrolled a ledger of levels they cleared for nothing, so the sentence it
   * has to earn is "and here is the rest of it", not "you have run out".
   *
   * Every number in it is an argument, including the price. The range moves
   * when a chapter ships and the price moves when the author decides it does
   * — it is read from the Worker at runtime (worker/stripe.ts) — so neither
   * is written into a sentence here, where it would be wrong in two languages
   * at once and would need a rebuild to fix.
   *
   * The currency moves too, which is the part that is easy to forget: the
   * course was priced in Egyptian pounds and is now priced in dollars, and
   * nothing in this block had to be rewritten for that, because no sentence
   * in it names a currency. `price` takes one and hands it to `Intl`. The
   * moment a sentence here says "pounds" it is a sentence that goes stale in
   * a config change nobody will think to grep for.
   */
  offer: {
    label: 'The rest of the course',
    /* The heading is the range when the catalogue can name one, and the size
       of the paid half when it cannot — the pair the hero's button is, and
       argued there. The lede below has always counted rather than pointed,
       which is why it needs no second version. */
    title: (from: number, to: number) => `Levels ${num(from, 'en')}-${num(to, 'en')}`,
    titleCount: (count: number) => `${num(count, 'en')} more level${count === 1 ? '' : 's'}`,
    /* `free` is how many levels this catalogue opens without the course
       (components/home/Offer.tsx), so the sentence is true for a stranger on
       the landing page and for a learner who has played them all. */
    lede: (levels: number, free: number) =>
      `The first ${num(free, 'en')} levels teach the tools. These ${num(levels, 'en')} levels are the job: a codebase you did not ` +
      `write, a pipeline that breaks, a review that disagrees with you, and an incident at the wrong hour — and ` +
      `then the AI Engineering track, where you review a coding agent's pull requests and build on a model.`,
    points: [
      'Every brief, hint ladder and debrief in the paid half, the whole AI Engineering track included.',
      'Both languages, and everything you have already cleared stays cleared.',
      'Three months of all of it for one payment, or one month to start. Nothing renews by itself: what comes after is your choice.',
      'Certificates are included.',
    ],
    /* Just the amount. A function because the currency and the digits are
       formatted per language, and `money` is where that is decided.

       It is also the one leaf in this catalogue whose Arabic can come out
       with no Arabic in it. `Intl` renders EGP as `ج.م.` and USD as `US$`,
       putting either one on the correct side of the number and adding the
       bidi marks that keep it there; which of the two the Arabic price holds
       is a fact about the currency this deployment charges in and not about
       the translation. web/test/strings.test.ts reaches the same conclusion
       from the other end, where it has to be written down as an exemption. */
    price: (amount: number, currency: string) => money(amount, currency, 'en'),
    priceWhy: 'For three months. Nothing renews by itself.',
    /* Under the price when it is shown in the reader's own currency
       (lib/format.ts, `approxMoney`): the figure the checkout really charges,
       which is the only exact number on the block. No currency is named, for
       the reason argued above `offer`; `amount` names it. */
    charged: (amount: string) => `You pay ${amount}; your bank converts it at its own rate.`,
    /** Beside the struck-through price, for a screen reader, which reads a `<s>` as plain text. */
    regularLabel: '(regular price)',
    /** Under the price where it is a regional one (docs/regional-pricing.md). */
    regional: (country: string) => `Regional price for ${country}.`,
    /** Under the price during the launch sale. The date is on the UTC calendar (lib/format.ts, `utcDate`). */
    sale: (percent: number, date: string) => `${num(percent, 'en')}% off until ${date}`,
    buy: 'Get the full course',
    /* The month (owner, 2026-09-29), drawn under the three months as the
       smaller of two choices: the same levels for a month, at a price the
       page compares rather than argues. `monthLabel` sits before its price,
       `monthWhy` under it with the saving worked out from the two prices
       (components/home/Offer.tsx), so no sentence here names a figure. */
    monthLabel: 'Or one month',
    monthWhy: (percent: number) => `Three months come to ${num(percent, 'en')}% less a month.`,
    monthBuy: 'Get one month',
    /* The `title` on the buy button (components/home/Offer.tsx): where the
       button goes, said before it goes there. A full-page redirect to a host
       the learner has never typed is the one moment in this product that can
       read as something going wrong, and naming the company is most of the
       answer to that.

       The wallets are named rather than gathered under "wallet" because
       "can I pay with Apple Pay" is a question somebody asks before pressing
       a button and not after — and because Stripe's hosted page offers them
       without anything being configured for it, which an embedded form would
       not have. Link is left out: it is Stripe's own saved-card service and
       the name means nothing to a first-time buyer. */
    buyWhy: 'Opens Stripe — card, Apple Pay or Google Pay',
    /* Said before the button is pressed rather than after, because being
       bounced to a sign-in you were not expecting is the worst moment in a
       checkout to discover you need an account. */
    signedOut: 'You will sign in first: the course unlocks an account, not a browser.',
    off: 'Card payments are not switched on here yet.',
    haveCode: 'I have an invite code',
    /**
     * For the reader the button cannot serve.
     *
     * Stripe's hosted page takes a card, Apple Pay and Google Pay, and a
     * great many people in the places this course is read from hold none of
     * the three — no card at all, or one their bank will not let out of the
     * country. Until this line existed the offer block's whole answer to them
     * was a price and a button they could not press, which is the same as
     * having no answer.
     *
     * It says the condition first and the way out second, so somebody who can
     * pay normally stops reading at the comma. It is deliberately not a
     * promise about *which* method will be found: what is being promised is
     * that a person will read the question and try, and the thing that would
     * be dishonest here is naming a wallet nobody has set up yet.
     *
     * Printed in both states of the block, including the one where the
     * checkout is off entirely — that reader has even less to go on.
     */
    noCard: 'No card, or one that will not go through? Tell us and we will find a way to pay that works where you are.',
    /** The control at the end of that sentence. It opens the support desk in
     *  place — a conversation with whoever runs this deployment, which on the
     *  landing page is what mounts the panel at all (lib/dock.ts). "Ask"
     *  rather than "contact", because what happens next is a question in a
     *  room and not a form that disappears. */
    noCardLink: 'Ask support',
  },

  /**
   * The account's period of the course (worker/entitlement.ts, `courseAccess`),
   * said on the ledger where the offer would be. The offer draws nothing for
   * an account that owns everything, so without this a learner would find out
   * their access had ended by finding the levels locked.
   *
   * `until` is the quiet line for most of the year. `renewSoon` replaces it
   * in the last fourteen days, which is when the Worker opens the checkout
   * again, and says the one thing that makes renewing early safe: the new
   * period starts where this one ends. `ended` sits over the offer once it is
   * back, so the price reads as "more of the course" and not as a stranger's.
   *
   * None of them says how long the running period is. Since the month
   * (2026-09-29) it can be one month or three, and the date is the only part
   * of that anybody needs.
   */
  term: {
    until: (date: string) => `Your access to the full course runs until ${date}.`,
    renewSoon: (date: string) =>
      `Your access to the full course ends on ${date}. Renewing now adds to it from that day, so you lose nothing by doing it early.`,
    ended: (date: string) =>
      `Your access to the full course ended on ${date}. Everything you cleared, your certificates and your public page are still yours.`,
    renew: 'Renew for three months',
    renewMonth: 'Renew for one month',
  },

  /**
   * The invite-code dialog (components/home/Invite.tsx).
   *
   * A code unlocks what a payment unlocks, so this dialog is the offer block's
   * equal and not a footnote to it: somebody arriving with a code in hand is a
   * reviewer, a student on a scholarship or a person in a room the author was
   * speaking to, and none of them should have to read a sales pitch first.
   *
   * The outcomes are written into the panel rather than thrown as toasts, for
   * the reason Backup's are: they answer something the person just did, and a
   * refusal in particular is worth being able to read twice.
   */
  invite: {
    label: 'Invite code',
    title: 'Unlock with a code',
    lede: 'A code unlocks exactly what a payment unlocks: every level in the paid half, on the account you are signed in to.',
    field: 'Your code',
    /* The shape, not a real one: thirteen groups after the prefix. */
    placeholder: 'EOS-XXXX-XXXX-XXXX-…',
    redeem: 'Unlock the course',
    redeeming: 'Checking…',
    close: 'Close',
    closeWhy: 'Nothing is redeemed until you press Unlock',
    signIn: 'Sign in',
    signedOut: 'Sign in first, so there is an account to unlock.',
    said: {
      /* What a code just bought, in the same two shapes as the offer above
         it: the rows it put on the map, or — when the paid half is not one
         lane's tail and has no range to point at — how many of them there
         are. The dialog used to fall back to `0` for both numbers when it
         could not read a span, which announced that levels 0 to 0 had
         arrived. A count cannot degrade that way. */
      redeemed: (from: number, to: number) =>
        `Unlocked. Levels ${num(from, 'en')}-${num(to, 'en')} are on the map now.`,
      redeemedCount: (count: number) =>
        `Unlocked. ${num(count, 'en')} ${count === 1 ? 'level is' : 'levels are'} on the map now.`,
      /* And when the dialog could read no paid half at all as the code
         landed — a catalogue this browser was already holding whole. It
         cannot name rows or a number, and it does not need to: the map
         behind the panel has every level on it. */
      redeemedAll: 'Unlocked. Every level in the course is on the map now.',
      already: 'This account already has the full course. Nothing was changed.',
      /* The one refusal worth acting on, so it is the one that says what to
         look for. The others deliberately do not hint at what a live code
         looks like. */
      malformed: 'That is not a complete code. There are 52 characters after the dashes — check whether one did not copy.',
      unknown: 'That code is not valid.',
      /* Says plainly that the code was real and is gone. Somebody holding a
         single-use code that has been redeemed is not making a mistake they
         can correct by looking harder at it, and "not valid" would send them
         back to check a string that is fine. The next move is to ask for
         another code, so the sentence says so. */
      spent: 'That code has already been used. Single-use codes work once — ask for another one.',
      anonymous: 'Sign in first, so there is an account to unlock.',
      unavailable: 'Invite codes are not switched on here.',
      failed: 'Could not check that code. Try again in a moment.',
    },
    privacy: 'A code is not a password. It unlocks the course on this account, and does nothing else.',
  },

  /* Rank, meter and the three figures. */
  standing: {
    label: 'Your standing',
    rank: 'Rank',
    rankMeter: 'Progress to next rank',
    toNextRank: (xp: number, rank: string) => `${num(xp, 'en')} XP to ${rank}`,
    topRank: 'Top rank reached',
    xp: 'XP',
    levels: 'Levels',
    stars: 'Stars',
    /* `<Counter>` is an element, so the ratio is parts rather than a string. */
    outOf: (done: ReactNode, total: number) => [done, `/${num(total, 'en')}`],
    localOnly: 'Saved in this browser only',
    localOnlyWhy: 'Clearing this site’s data would take it with everything else.',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
  },

  /* The ledger's own heading. */
  ledger: {
    title: 'The course',
    note:
      'Cleared worlds are folded up. Three stars without hints, two with up to three, one after the solution. ' +
      'Use any tool you like on a level, AI included: the grade is what you leave in the repository.',
  },

  /* One world's header, its opener card, and the meter on it. */
  world: {
    count: (cleared: number, total: number) => `${num(cleared, 'en')} / ${num(total, 'en')}`,
    /* Decorative: the glyph is aria-hidden, the count beside it is not. */
    starCount: (stars: number) => ` · ${num(stars, 'en')}★`,
    meterLabel: (world: string, cleared: number, total: number) =>
      `${world}: ${num(cleared, 'en')} of ${num(total, 'en')} cleared`,
    /**
     * The mark on a folded chapter whose checkpoint is open and not passed.
     *
     * An offer and not an instruction, which is the whole register a
     * checkpoint has to be written in: it never blocks, and a learner who
     * skips it continues exactly as before (docs/checkpoints.md, rule 1). So
     * "waiting", not "required", and nothing here counts down or nags.
     */
    checkpointWaiting: (world: string) => `${world}: the checkpoint for this chapter is open, and not passed yet`,
    openerLabel: 'Start here',
    openerMinutes: (minutes: number) => `${num(minutes, 'en')} min`,
  },

  /* One row of the ledger. */
  row: {
    label: (level: number, title: string) => `Level ${num(level, 'en')}: ${title}`,
    labelPaid: (level: number, title: string) => `Level ${num(level, 'en')}: ${title} — part of the full course`,
    /**
     * A checkpoint row, which takes no level number.
     *
     * It has one — its own track is numbered like any other — but that number
     * counts checkpoints, not levels, and the row is drawn at the foot of a
     * chapter whose levels have just counted past twelve
     * (components/home/LevelRow.tsx). So the sentence names the row's kind
     * instead of its position, which is the thing that was worth saying.
     *
     * The title arrives already reading "Checkpoint: Recovery", in both
     * languages, so what follows it is a placement rather than a repetition:
     * this one belongs to the chapter you are looking at.
     */
    labelCheckpoint: (title: string) => `${title}: the checkpoint for this chapter`,
    labelCheckpointPaid: (title: string) =>
      `${title}: the checkpoint for this chapter — part of the full course`,
    unverified: 'unverified',
    /* `why` is what progress recorded, in its own words. */
    unverifiedWhy: (why?: string) =>
      `This clear could not be verified${why ? `: ${why}` : ''}. Replay the level to verify it.`,
    paid: 'full course',
    /**
     * The range is an argument, like every other range in this file.
     *
     * This one sentence was written with `20-49` inside it, which was true on
     * the day it was written and stopped being true the moment a chapter
     * shipped: the free half ends where the first locked level starts and the
     * course ends at its last level, and both of those move. It went wrong in
     * two languages at once and needed a rebuild to fix, which is the whole
     * argument for `paidSpan` in lib/entitlement — the row is handed the
     * numbers the catalogue actually reports rather than the ones somebody
     * remembered.
     *
     * The second version is for the row whose own lane has no range to quote
     * — a wholly paid track, or a catalogue with locked levels in two lanes
     * at once. It says the size of the paid half and places this row inside
     * it, which is the true part of the sentence the range was carrying.
     */
    paidWhy: (from: number, to: number) =>
      `Part of the full course. Unlock levels ${num(from, 'en')}-${num(to, 'en')} to play it.`,
    paidWhyCount: (count: number) =>
      `Part of the full course. Unlocking it opens ${num(count, 'en')} level${count === 1 ? '' : 's'}, this one among them.`,
    skipped: 'skipped',
    skippedWhy: 'You moved past this one. It is still here, with its stars unclaimed.',
    minutes: (minutes: number) => `${num(minutes, 'en')} min`,
    xpEarned: (xp: number) => `${num(xp, 'en')} XP`,
    xpOffered: (xp: number) => `+${num(xp, 'en')} XP`,
    /* "Debrief", then the rest of the sentence for a screen reader only. */
    debrief: (hidden: ReactNode) => ['Debrief', hidden],
    debriefFor: (level: number, title: string) => ` for level ${num(level, 'en')}, ${title}`,
    /* No number here either, and for the reason `labelCheckpoint` gives. */
    debriefForCheckpoint: (title: string) => ` for ${title}`,
  },

  footer: {
    fiction: 'Lattice, Priya and Marcus are fictional. The repository, the shell and the git are real. Iris and Kite are designed agents. Blobor is our own game.',
    unverifiedNote: 'Hollow stars are clears that could not be verified. Replay the level to fill them in.',
    starsNote: 'Three stars without hints. Two with up to three hints. One after the solution.',
    /** The three documents, as labels rather than sentences. They are built to
     *  static files outside the bundle (scripts/build-legal.mjs), so these are
     *  plain links the router deliberately ignores — it claims only `?`-hrefs —
     *  and the browser navigates to them properly. */
    legal: 'Terms',
    privacy: 'Privacy',
    refunds: 'Refunds',
    /** The pitch, which this page stopped carrying when it split in two. A
     *  learner wants it roughly never, and when they do it is to send it to
     *  somebody — so it is a footer link and not a bar one. */
    about: 'About EngineerOS',
  },

  /* --- The landing page ---------------------------------------------------
     Read by somebody who has never been here. The hero above it is shared with
     nothing else on this page — `hero` is the landing page's now — and what is
     below is the chapters, which are the argument: a curriculum you can read
     is the only claim on this page that checks itself. */
  landing: {
    game: {
      label: 'Where the practice leads',
      title: 'Meet Blobor, our game.',
      lede: 'We also make Blobor, a multiplayer game set in Luma. The skills you practise with Iris can help us test it, find bugs and build small pieces of it.',
      play: 'Play Blobor',
      artSource: 'From the Luma story trailer',
      artAlt: 'A green Blobor on a stone watchtower overlooks Luma’s islands and harbors at sunset.',
    },
    /** The bar's own link down to the chapters. */
    theCourse: 'The course',
    /** The two narrated bands under the hero (components/home/WhyVideo.tsx):
     *  first why learn engineering at all (explainer/swimmer-and-ship.tsx),
     *  then why learn it here (explainer/why-engineering.tsx). */
    ageLabel: 'Why agents',
    ageTitle: 'Why direct AI agents?',
    whyLabel: 'Why EngineerOS',
    whyTitle: 'Why learn it with EngineerOS?',
    /** The hero's link down to the first band. */
    whyWatch: 'Watch: why engineers now direct agents',
    /** Under the one call to action. It says the gate on the free half in the
     *  same breath as the invitation, because the alternative is a reader who
     *  finds out on the far side of a click. */
    ctaNote: 'No account needed. The first level runs in this tab.',
    /** The bar's own link down to the price. Drawn only when there is one. */
    pricing: 'Pricing',
    /** The heading over the chapter grid, and the line under it. */
    courseTitle: 'The course',
    courseNote:
      'Every level is a task inside a repository. These are the chapters, in order: from your first commit to ' +
      'reviewing what a coding agent opens.',
    /**
     * The way to the list itself: beside the hero's call to action, and at
     * the head of the chapter grid.
     *
     * The chapters are the course grouped and argued; the ledger is the course
     * enumerated, a numbered row per level. Until this link existed the only
     * way to reach that list from here was to start a level and find the way
     * back out of it.
     *
     * The note is not decoration. `?view=course` is the app, and the app boots
     * a virtual machine — so the link says what pressing it costs, in the same
     * breath as what it gives. A page that spends its length refusing to
     * oversell cannot start a 28MB download under somebody who asked to see a
     * list.
     */
    allLevels: (n: number) => `See all ${num(n, 'en')} levels`,
    allLevelsNote: 'The whole list, level by level, with what each one takes. It opens the course, so the machine starts too.',
    /** Phones only (home.css): the chapter grid shows three until this is pressed. */
    showAllWorlds: (n: number) => `Show all ${num(n, 'en')} chapters`,
    /** The guided first command on the landing page (components/home/LandingTry.tsx).
     *  It is level 0's own `ls` card, not Iris's first proposal there (which
     *  is `cat START_HERE.md`), so the title says "your first command" and the
     *  lede says where Iris comes in rather than claiming this is hers. */
    tryLabel: 'Try it here',
    tryTitle: 'Run your first command',
    tryLede: 'On Level 0 Iris proposes the commands and you decide what runs. Try one here first: no download, no account. Type it and press Enter.',
    tryAfter: 'That is what the terminal shows on Level 0, where Iris proposes the next commands and you decide. The level itself runs on a real machine in this tab.',
    /** On a chapter this browser was not given. Not "locked": the card is not
     *  a door somebody is being refused, it is a description of what the paid
     *  half contains, read by somebody who has not bought anything yet. */
    fullCourse: 'Full course',
    levelCount: (n: number) => `${num(n, 'en')} ${n === 1 ? 'level' : 'levels'}`,
    minutes: (n: number) => `≈ ${num(n, 'en')} min`,
    /** The learner who signed in on a browser with no record on it yet — a new
     *  laptop, or the round trip home from Google. lib/landing.ts cannot know
     *  about them before the first paint, so this is where they are met. */
    signedInAs: (name: string) => `Signed in as ${name}.`,
    /** And the reader this page now also has to meet: somebody with a record
     *  in this browser, who used to be routed past the pitch entirely and
     *  arrives on it like everybody else since lib/landing.ts stopped asking.
     *  The band is their way out of the page and it goes to the ledger; the
     *  button in the hero goes to the level. Two destinations, on purpose. */
    resumeAt: (level: number) => `You left off at Level ${num(level, 'en')}.`,
    /** The same band, for a record with no open level left in it: everything
     *  they hold is cleared, or the catalogue cannot name a frontier. */
    resumeAny: 'You have progress saved in this browser.',
    toCourse: 'Open your course',
    /** The call to action when the catalogue could not name a first level. */
    open: 'Open the course',
  },

  /* --- Where you were -----------------------------------------------------
     The card at the top of the ledger, in place of the hero the landing page
     took. The button's own label and the line under it are still `hero`'s —
     all four states of them — so only the frame is here: which chapter, and
     which level. */
  upnext: {
    label: 'Up next',
    place: (chapter: string, level: number) => `${chapter} · Level ${num(level, 'en')}`,
  },

  /**
   * The fourteen chapters, keyed by the `world:` string in mission.yaml.
   *
   * The key is a join, not a heading: the same string is in fifty missions,
   * in the openers' frontmatter and in `openerForWorld`, so it stays English
   * in both catalogues and only the `name` beside it is translated
   * (docs/i18n.md §6.1). A world with no entry here renders its heading and
   * no line — not an error, but it reads as an unfinished page, so a new
   * world name belongs in both halves of this map on the day its first
   * mission lands.
   */
  worlds: {
    'The Workstation': {
      name: 'The Workstation',
      tagline: 'The machine under your hands. Where you are, what is there, and how to read it.',
    },
    'First Commits': {
      name: 'First Commits',
      tagline: 'Where git starts. A folder becomes a history, and you choose what goes into it.',
    },
    'Real Tasks': {
      name: 'Real Tasks',
      tagline: 'Real work for your agent: a red test suite, a crash, a break hidden in history, in a codebase too big to read whole.',
    },
    'Branches & Remotes': {
      name: 'Branches & Remotes',
      tagline: 'Other people exist. Push, pull, and the conflict everyone dreads.',
    },
    Recovery: {
      name: 'Recovery',
      tagline: 'Nothing is lost. Reflog, rebase, and switching work without dropping it.',
    },
    Quality: {
      name: 'Quality',
      tagline: 'Tests as a map, stack traces as directions, and proof that bugs stay dead.',
    },
    'Team Git': {
      name: 'Team Git',
      tagline: 'Cherry-pick, bisect, squash, and ship a full ticket on your own.',
    },
    'Code Review': {
      name: 'Code Review',
      tagline: 'The diff someone else has to read, and the review you have to answer.',
    },
    'The Codebase': {
      name: 'The Codebase',
      tagline: 'Code nobody remembers writing. Blame it, rename it, delete it, split it.',
    },
    Releases: {
      name: 'Releases',
      tagline: 'The moment changes become a thing customers have. Tags, notes, hotfixes, rollbacks.',
    },
    Production: {
      name: 'Production',
      tagline: 'An alert at 2am. Read the logs, reproduce it, stop the bleeding, write it up.',
    },
    'The Pipeline': {
      name: 'The Pipeline',
      tagline: 'One command to check it, a hook that runs it, and CI that runs it without you.',
    },
    Data: {
      name: 'Data',
      tagline: 'The part you cannot revert. Migrate forwards, survive a second run, lose nothing.',
    },
    Design: {
      name: 'Design',
      tagline: 'The risky changes: a flag, a contract someone else depends on, a measurement.',
    },
    Ownership: {
      name: 'Ownership',
      tagline: 'The service is yours now. Say no to a dependency, write the runbook, run the night.',
    },
    'The Platform': {
      name: 'The Platform',
      tagline: 'Most of what you need to change belongs to somebody else. Fork it, open the request, and meet a branch that says no.',
    },
    'The Program': {
      name: 'The Program',
      tagline: 'A file is not a process. What a program becomes the moment it runs, and why editing the file afterwards reaches nothing.',
    },
    'The Network': {
      name: 'The Network',
      tagline: 'Read what is on the wire, mean the status line, and put a deadline on everything that leaves.',
    },
    'The Database': {
      name: 'The Database',
      tagline: 'Ask the data itself, read the plan before adding an index, and say when two writes are one fact.',
    },
    'The API': {
      name: 'The API',
      tagline: 'Design for people you cannot deploy for: addresses, versions, bounded collections, errors a program can read.',
    },
    'Security': {
      name: 'Security',
      tagline: 'Revoke before you tidy, never let a value become a sentence, and ask may they as well as who.',
    },
    'Concurrency': {
      name: 'Concurrency',
      tagline: 'Read, decide and write is three things; make it one, and expect every job to arrive twice.',
    },
    'Observability': {
      name: 'Observability',
      tagline: 'A mean is about everybody and a complaint is about somebody; find the gap nobody owns, and page only for what needs a person now.',
    },
    'Scale & Cost': {
      name: 'Scale and cost',
      tagline: 'A cache key is a claim, one request at a time is not load, and the bill is arithmetic somebody has to do.',
    },
    'Legacy': {
      name: 'Legacy',
      tagline: 'Describe it before you change it, pin the mistakes too, put the seam in first, and ramp it by something that cannot change its mind.',
    },
    'Configuration': {
      name: 'Configuration',
      tagline: 'One artefact everywhere, the environment tells it where it is, and a build you can reproduce is the only way to know what is running.',
    },
    'The Craft': {
      name: 'The Craft',
      tagline: 'Cut where the reasons to change differ, write the loop three times, put the unit in the name, and rank debt by what it has cost.',
    },
    'The Engineer': {
      name: 'The Engineer',
      tagline: 'Estimate from the log, find the question behind the ask, file the answer where the next person will find it, and say no by fixing the real thing.',
    },
    'The Whole Job': {
      name: 'The Whole Job',
      tagline: 'Mitigate before you understand and lift it afterwards, ship it off with a way to see it working, and leave a system that is fine without you.',
    },
    /* The security track's four chapters, and the checkpoint lane. They are
       `world:` strings like any other and they are drawn like any other — the
       chapter heading on the map, the `Hud`, the debrief and the profile all
       read this map and fall back to the English key when a world is not in
       it. That fallback is why these were missing for so long without anybody
       filing a bug: in English it is invisible, and in Arabic it is an
       English chapter name in the middle of an Arabic page (docs/i18n.md
       §6.1). */
    Checkpoints: {
      name: 'Checkpoints',
      tagline: 'One problem per chapter, with no hints and no walkthrough. The chapter is the preparation.',
    },
    'Trust the input': {
      name: 'Trust the input',
      tagline: 'Everything from outside is a claim. Name what can go wrong, and stop a value becoming a sentence.',
    },
    'Who you are, and who says so': {
      name: 'Who you are, and who says so',
      tagline: 'Identity is two questions, not one: who is this, and may they. Sessions, ids in URLs, and what a login form gives away.',
    },
    'Blast radius': {
      name: 'Blast radius',
      tagline: 'Assume it gets out. Scope the token, name the tenant in every query, and be able to say who did that.',
    },
    'Working securely with other people': {
      name: 'Working securely with other people',
      tagline: 'Read a diff for what it lets somebody do, and count a dependency, a bill and a stranger’s email as part of the system.',
    },
    /* The AI Engineering track's four chapters (docs/ai-track.md). */
    "The agent's pull request": {
      name: 'The agent’s pull request',
      tagline: 'Green, confident and wrong. Check every claim against the diff, and make the mistake impossible to merge twice.',
    },
    'Calling the model': {
      name: 'Calling the model',
      tagline: 'A 200 is not an answer. Validate the shape, read why it stopped, pay for the prefix once, and measure before you believe.',
    },
    'Giving it tools': {
      name: 'Giving it tools',
      tagline: 'Run the loop, keep the authority in code, treat what it read as data, and stop it before the bill does.',
    },
    'Answers from our own documents': {
      name: 'Answers from our own documents',
      tagline: 'Retrieve the right page, cite it or say you do not know, pin the model, and own the incident when it still goes wrong.',
    },
  } as Record<string, { name: string; tagline: string }>,
};

/**
 * Arabic counts its nouns, and this is the reason a sentence with a number
 * in it is a function.
 *
 * 3 to 10 take the plural (`5 دقائق`); 11 and up take the singular
 * (`15 دقيقة`). One and two take their own forms, which none of the figures
 * on this screen reach — a level is never "1 minute" and the course is never
 * two levels long — so the singular covers them.
 */
const plural = (n: number, few: string, one: string) => (n >= 3 && n <= 10 ? few : one);

export const ar: typeof en = {
  irisIntro: {
    role: 'وكيلتك المصمَّمة للذكاء الاصطناعي', meet: 'تعرّف على Iris',
    greeting: 'أهلًا، أنا Iris. أنت توجّه، وأنا أكتب.',
    artAlt: 'Iris، روبوت بلون الجرافيت والعاج وعين خضراء واحدة، ترحّب بك.',
    pause: 'إيقاف الحركة', play: 'تشغيل الحركة',
    title: 'أنت توجّه. و Iris تكتب.',
    body: 'تقترح Iris كل أمر، والقرار لك: وافق عليه، أو ارفضه، أو أخبرها بما تفعله بدلًا منه. صُمّمت لتخطئ أحيانًا، فالمهارة التي تبنيها هي الحكم على عمل الوكيل.',
    principle: 'اقرأ ما تقترحه، واسأل لماذا حين لا يكون واضحًا، ولا توافق أبدًا على أمر لا تستطيع شرحه.',
    cta: 'ابدأ البناء', preview: 'مثال على التعامل', choose: 'استكشف توجيه Iris',
    access: (through: number) => `في المستويات من 0 إلى ${num(through, 'ar')} تكتب Iris الأوامر، وأنت توجّهها وتقرّر ما يُشغَّل.`,
    note: (_agentFrom: number) => `هذه أمثلة على التعامل معها. تقترح Iris أوامر لا تُشغَّل إلا بموافقتك، من المستوى الأول عبر الفصول الأولى وفي كل نقطة تفتيش. ومثل أي وكيل، تخطئ أحيانًا، فتتدرّب على مراجعة عملها.`,
    examples: [
      {label:'كلّفها',question:'الاختبارات تفشل على main. اعرفي السبب، لكن لا تغيّري شيئًا بعد.',answer:`أقترح تشغيل ${ltr('npm test')} وقراءة أول فشل. هذا للقراءة فقط، فلن يتغيّر شيء. هل توافق؟`,takeaway:'حدّد الهدف والحدود، وهي تقترح الخطوة الأولى.'},
      {label:'وافق أو ارفض',question:`تريدين تنفيذ ${ltr('force push')}. لماذا؟`,answer:`لأستبدل الفرع البعيد بفرعنا. معك حق أن توقفني: سيُسقط ذلك commits التي كتبتها Priya. سأنفّذ ${ltr('fetch')} ثم ${ltr('rebase')} بدلًا منه.`,takeaway:'ارفض ما لن توقّع عليه، وقل ما تفعله بدلًا منه.'},
      {label:'افحص عملها',question:'تقولين إن الإصلاح انتهى. أريني.',answer:`التغيير يمسّ ملفًا واحدًا، و ${ltr('npm test')} ينجح. لم أضف اختبارًا للمُدخل الفارغ. هل أضيفه؟`,takeaway:'الإنجاز يعني دليلًا: اقرأ التغييرات، وشغّل الفحوص.'},
    ],
  },
  bar: {
    notSynced: 'لم تتم المزامنة',
    notSyncedWhy: 'لم يصل آخر تغيير إلى حسابك. سنحاول مرة أخرى.',
    restoreCode: 'معك رمز محفوظ؟',
    restoreCodeWhy: 'الصق رمزًا حفظته سابقًا، وتنضمّ مستوياته إلى هذا المتصفح',
  },

  hero: {
    title: (br: ReactNode) => ['اعمل مع وكلاء الذكاء الاصطناعي.', br, 'وأنجز المهمة.'],
    ctaStart: (level: number) => `ابدأ · المستوى ${num(level, 'ar')}`,
    ctaContinue: (level: number) => `تابع · المستوى ${num(level, 'ar')}`,
    /* The pair, as the English pair is argued. In the count version the
       adjective travels inside `plural` rather than being appended after it:
       `مستويات` is a broken plural and takes a feminine singular (`متبقية`),
       while the counted singular after 11-99 takes the accusative (`مستوى
       متبقيًا`), and an adjective stuck on the end would agree with one of
       them and not the other.

       Neither takes an isolate. The count has a digit run with Arabic on
       both sides of it, and the range is two digit runs joined by a hyphen,
       which bidi already reads as one left-to-right number — the argument
       `row.paidWhy` below sets out in full. */
    ctaUnlock: (from: number, to: number) => `افتح المستويات ${num(from, 'ar')}-${num(to, 'ar')}`,
    ctaUnlockCount: (count: number) => `افتح ${num(count, 'ar')} ${plural(count, 'مستويات متبقية', 'مستوى متبقيًا')}`,
    ctaReplay: 'أنجزت كل المستويات · العب من جديد',
    ctaIdle: 'ابدأ',
    noteNext: (level: number, minutes: number) =>
      `المستوى ${num(level, 'ar')} يستغرق نحو ${num(minutes, 'ar')} ${plural(minutes, 'دقائق', 'دقيقة')}.`,
    noteUnlock: (owned: number, from: number, to: number) =>
      `أنجزت المستويات المتاحة لك وعددها ${num(owned, 'ar')}. أكمل الدورة بفتح المستويات ${num(from, 'ar')}-${num(to, 'ar')}.`,
    noteUnlockCount: (owned: number, count: number) =>
      `أنجزت المستويات المتاحة لك وعددها ${num(owned, 'ar')}. وفي الدورة الكاملة ${num(count, 'ar')} ${plural(count, 'مستويات أخرى', 'مستوى آخر')}.`,
    noteReplay: 'أنجزت كل المستويات. يمكنك إعادة أي مستوى لتحسين نتيجتك.',
    noteIdle: 'جارٍ تحميل المستويات.',
  },

  offer: {
    label: 'بقية الدورة',
    title: (from: number, to: number) => `المستويات ${num(from, 'ar')}-${num(to, 'ar')}`,
    titleCount: (count: number) => `${num(count, 'ar')} ${plural(count, 'مستويات إضافية', 'مستوى إضافي')}`,
    lede: (levels: number, free: number) =>
      `أول ${num(free, 'ar')} ${plural(free, 'مستويات', 'مستوى')} تعلّمك الأدوات. وهذه ${num(levels, 'ar')} ${plural(levels, 'مستويات', 'مستوى')} هي العمل نفسه: ` +
      `شيفرة لم تكتبها، و ${ltr('CI pipeline')} يتعطّل، ومراجعة لا توافقك، وحادثة في وقت غير مناسب، ` +
      `ثم مسار هندسة الذكاء الاصطناعي، حيث تراجع pull requests يفتحها وكيل برمجي وتبني فوق نموذج.`,
    points: [
      'كل إحاطة وسُلّم تلميحات وخلاصة في النصف المدفوع، ومعها مسار هندسة الذكاء الاصطناعي كاملًا.',
      'باللغتين، وكل ما أنجزته يبقى منجزًا.',
      'ثلاثة أشهر من كل ذلك مقابل دفعة واحدة، أو شهر واحد للبداية. لا شيء يتجدّد تلقائيًا: وما بعد ذلك قرارك.',
      'الشهادات مشمولة.',
    ],
    price: (amount: number, currency: string) => money(amount, currency, 'ar'),
    priceWhy: 'لثلاثة أشهر. لا شيء يتجدّد تلقائيًا.',
    charged: (amount: string) => `المبلغ المدفوع فعليًا ${amount}، ويحوّله مصرفك إلى عملتك بسعر الصرف لديه.`,
    regularLabel: '(السعر المعتاد)',
    regional: (country: string) => `السعر الإقليمي في ${country}.`,
    sale: (percent: number, date: string) => `خصم ${num(percent, 'ar')}٪ حتى ${date}`,
    buy: 'احصل على الدورة كاملة',
    monthLabel: 'أو شهر واحد',
    monthWhy: (percent: number) => `الأشهر الثلاثة أرخص بنسبة ${num(percent, 'ar')}٪ للشهر الواحد.`,
    monthBuy: 'احصل على شهر واحد',
    /* Three Latin runs, and all three are company names, which §2 of
       docs/i18n.md keeps in Latin. `Apple Pay` in particular is the label
       printed on the button the learner is about to press on Stripe's own
       page, and an Arabic paraphrase of it would be a name for something they
       then have to recognise in Latin a second later. `محفظة` is still the
       glossary's word for a wallet and it is what the sentence would fall
       back to if the names ever stopped being worth saying.

       None of the three takes an isolate, which is the line `footer.fiction`
       already draws: `Lattice`, `Priya` and `Marcus` stand in it bare while
       `shell` beside them is wrapped. A name is an ordinary word — letters,
       no punctuation, Arabic on the other side of the space — and bidi
       resolves it as one left-to-right run without being told to. The isolate
       is for what a name is not: a path, a lone `+`, anything ending in
       punctuation. `Google Pay` closes the sentence with nothing after it for
       the algorithm to put on the wrong end. */
    buyWhy: 'سينقلك إلى Stripe — بطاقة أو Apple Pay أو Google Pay',
    signedOut: 'ستسجّل الدخول أولًا: الدورة تُفتح لحسابك، لا لمتصفحك.',
    off: 'الدفع بالبطاقة غير مفعّل هنا بعد.',
    haveCode: 'لديّ رمز دعوة',
    noCard: 'لا تملك بطاقة، أو بطاقتك لا تمر؟ أخبرنا ونجد معك طريقة دفع تناسب بلدك.',
    noCardLink: 'اسأل الدعم',
  },

  term: {
    until: (date: string) => `وصولك إلى الدورة كاملة سارٍ حتى ${date}.`,
    renewSoon: (date: string) =>
      `ينتهي وصولك إلى الدورة كاملة في ${date}. التجديد الآن يُضاف بدءًا من ذلك اليوم، فلا تخسر شيئًا إن جدّدت مبكرًا.`,
    ended: (date: string) =>
      `انتهى وصولك إلى الدورة كاملة في ${date}. كل ما أنجزته وشهاداتك وصفحتك العامة ما زالت لك.`,
    renew: 'جدّد لثلاثة أشهر',
    renewMonth: 'جدّد لشهر واحد',
  },

  invite: {
    label: 'رمز الدعوة',
    title: 'افتح الدورة برمز',
    lede: 'الرمز يفتح ما يفتحه الشراء تمامًا: كل مستويات النصف المدفوع، على الحساب الذي سجّلت الدخول به.',
    field: 'رمزك',
    placeholder: 'EOS-XXXX-XXXX-XXXX-…',
    redeem: 'افتح الدورة',
    redeeming: 'جارٍ التحقق…',
    close: 'إغلاق',
    closeWhy: 'لا يُستخدم الرمز قبل الضغط على «افتح الدورة»',
    signIn: 'سجّل الدخول',
    signedOut: 'سجّل الدخول أولًا ليكون هناك حساب تُفتح له الدورة.',
    said: {
      redeemed: (from: number, to: number) =>
        `تم الفتح. المستويات ${num(from, 'ar')}-${num(to, 'ar')} صارت على الخريطة الآن.`,
      /* `أصبح لديك` puts the verb before the count, which is where Arabic
         lets a verb stay singular whatever follows it. A verb agreeing
         backwards with `مستويات` and then with `مستوى` would need a third
         form in `plural` for no gain in meaning. */
      redeemedCount: (count: number) =>
        `تم الفتح. أصبح لديك ${num(count, 'ar')} ${plural(count, 'مستويات', 'مستوى')} على الخريطة الآن.`,
      redeemedAll: 'تم الفتح. كل مستويات الدورة على الخريطة الآن.',
      already: 'هذا الحساب لديه الدورة كاملة بالفعل. لم يتغيّر شيء.',
      malformed: 'هذا ليس رمزًا كاملًا. الرمز 52 حرفًا من دون الشرطات — تأكّد من أن النسخ لم يُسقط حرفًا.',
      unknown: 'هذا الرمز غير صالح.',
      /* `استُخدم` rather than a passive built with `تمّ`: the point of the
         sentence is that the code is finished, and the plain passive verb
         carries that where `تمّ استخدامه` would read as a receipt. */
      spent: 'هذا الرمز استُخدم من قبل. الرمز ذو الاستخدام الواحد يعمل مرة واحدة — اطلب رمزًا آخر.',
      anonymous: 'سجّل الدخول أولًا ليكون هناك حساب تُفتح له الدورة.',
      unavailable: 'رموز الدعوة غير مفعّلة هنا.',
      failed: 'تعذّر التحقق من الرمز. حاول بعد قليل.',
    },
    privacy: 'الرمز ليس كلمة مرور. يفتح الدورة على هذا الحساب فقط، ولا يفعل شيئًا غير ذلك.',
  },

  standing: {
    label: 'تقدمك المهني',
    rank: 'الرتبة',
    rankMeter: 'التقدم نحو الرتبة التالية',
    toNextRank: (xp: number, rank: string) => `${num(xp, 'ar')} ${plural(xp, 'نقاط', 'نقطة')} خبرة حتى ${rank}`,
    topRank: 'بلغت أعلى رتبة',
    xp: 'نقاط الخبرة',
    levels: 'المستويات',
    stars: 'النجوم',
    outOf: (done: ReactNode, total: number) => [done, `/${num(total, 'ar')}`],
    localOnly: 'محفوظ في هذا المتصفح فقط',
    localOnlyWhy: 'سيُمحى تقدمك إذا مسحت بيانات هذا الموقع.',
    signIn: 'سجّل الدخول',
    signingIn: 'جارٍ تسجيل الدخول…',
  },

  ledger: {
    title: 'الدورة',
    note:
      'تُطوى الفصول المنجزة. تحصل على 3 نجوم من دون تلميحات، ونجمتين عند استخدام ثلاثة تلميحات بحد أقصى، ونجمة بعد كشف الحل. ' +
      'استخدم أي أداة تريدها في المستويات، بما فيها الذكاء الاصطناعي: التقييم على ما تتركه في المستودع.',
  },

  world: {
    count: (cleared: number, total: number) => `${num(cleared, 'ar')} / ${num(total, 'ar')}`,
    starCount: (stars: number) => ` · ${num(stars, 'ar')}★`,
    meterLabel: (world: string, cleared: number, total: number) =>
      `${world}: أنجزت ${num(cleared, 'ar')} من ${num(total, 'ar')}`,
    checkpointWaiting: (world: string) => `${world}: محطة هذا الفصل متاحة ولم تجتزها بعد`,
    openerLabel: 'ابدأ بهذا التمهيد',
    openerMinutes: (minutes: number) => `${num(minutes, 'ar')} ${plural(minutes, 'دقائق', 'دقيقة')}`,
  },

  row: {
    label: (level: number, title: string) => `المستوى ${num(level, 'ar')}: ${title}`,
    labelPaid: (level: number, title: string) => `المستوى ${num(level, 'ar')}: ${title} — جزء من الدورة الكاملة`,
    /* "محطة" is the course-wide word for a checkpoint — it is what the
       checkpoint titles and modules already say in their Arabic sidecars, so
       the sentence has to agree with the title it is wrapping. */
    labelCheckpoint: (title: string) => `${title}: محطة هذا الفصل`,
    labelCheckpointPaid: (title: string) => `${title}: محطة هذا الفصل — جزء من الدورة الكاملة`,
    unverified: 'غير مؤكّد',
    unverifiedWhy: (why?: string) =>
      `تعذّر تأكيد إنجاز هذا المستوى${why ? `: ${why}` : ''}. أعده لتأكيد النتيجة.`,
    paid: 'الدورة الكاملة',
    /* The two numerals go through `num` like every other number here, and the
       range is left unisolated for the same reason `ctaUnlock` and the
       offer's title leave theirs: `20-51` is two digit runs joined by a
       hyphen, which bidi already reads as one left-to-right number. The
       isolate is for what a hyphen is not — `xpOffered`'s lone `+`, a path,
       a branch name. */
    paidWhy: (from: number, to: number) =>
      `هذا المستوى ضمن الدورة الكاملة. افتح المستويات ${num(from, 'ar')}-${num(to, 'ar')} للبدء به.`,
    /* The count version has no hyphen in it and no Latin token, so there is
       even less for bidi to decide about than in the range above: one digit
       run with Arabic on both sides of it. */
    paidWhyCount: (count: number) =>
      `هذا المستوى ضمن الدورة الكاملة. بفتحها تحصل على ${num(count, 'ar')} ${plural(count, 'مستويات', 'مستوى')}، هذا المستوى منها.`,
    skipped: 'تم تخطيه',
    skippedWhy: 'تخطيت هذا المستوى، لكنه ما زال متاحًا ويمكنك العودة لنيل نجومه.',
    minutes: (minutes: number) => `${num(minutes, 'ar')} ${plural(minutes, 'دقائق', 'دقيقة')}`,
    xpEarned: (xp: number) => `${num(xp, 'ar')} ${plural(xp, 'نقاط', 'نقطة')} خبرة`,
    /* The `+` is isolated: a lone sign before a number in a right-to-left
       run comes out on the wrong side of it, and `150+` says something else. */
    xpOffered: (xp: number) => `${ltr(`+${num(xp, 'ar')}`)} ${plural(xp, 'نقاط', 'نقطة')} خبرة`,
    debrief: (hidden: ReactNode) => ['الخلاصة', hidden],
    debriefFor: (level: number, title: string) => ` للمستوى ${num(level, 'ar')}، ${title}`,
    debriefForCheckpoint: (title: string) => ` لـ${title}`,
  },

  footer: {
    fiction: `Lattice و Priya و Marcus شخصيات خيالية. أما المستودع و ${ltr('shell')} و Git فحقيقية. Iris و Kite وكيلان مصمَّمان، و Blobor لعبتنا نحن.`,
    unverifiedNote: 'النجوم الفارغة تعني أن النتيجة لم تُؤكّد. أعد المستوى لتأكيدها.',
    starsNote: '3 نجوم بلا تلميحات. نجمتان مع ثلاثة تلميحات بحد أقصى. نجمة واحدة بعد كشف الحل.',
    legal: 'الشروط',
    privacy: 'الخصوصية',
    refunds: 'الاسترداد',
    about: 'عن EngineerOS',
  },

  /* --- The landing page --------------------------------------------------- */
  landing: {
    game: {
      label: 'إلى أين يقودك التدريب',
      title: 'تعرّف على Blobor، لعبتنا.',
      lede: 'نصنع أيضًا Blobor، وهي لعبة جماعية تدور في Luma. المهارات التي تتدرّب عليها مع Iris يمكن أن تساعدنا في اختبارها، واكتشاف أخطائها، وبناء أجزاء صغيرة منها.',
      play: 'العب Blobor',
      artSource: 'من الفيديو التعريفي بقصة Luma',
      artAlt: 'Blobor أخضر فوق برج مراقبة حجري يطلّ على جزر Luma وموانئها عند الغروب.',
    },
    theCourse: 'المسار',
    ageLabel: 'لماذا الوكلاء',
    ageTitle: 'لماذا توجّه وكلاء الذكاء الاصطناعي؟',
    whyLabel: 'لماذا EngineerOS',
    whyTitle: 'لماذا تتعلّمها مع EngineerOS؟',
    whyWatch: 'شاهد: لماذا صار المهندسون يوجّهون الوكلاء',
    ctaNote: 'لا حاجة إلى حساب. المستوى الأول يعمل داخل هذا التبويب.',
    pricing: 'السعر',
    courseTitle: 'المسار',
    courseNote: 'كل مستوى مهمّة داخل مستودع. وهذه فصوله، بالترتيب: من أول commit لك إلى مراجعة ما يفتحه وكيل برمجي.',
    allLevels: (n: number) => `اعرض كل المستويات (${num(n, 'ar')})`,
    allLevelsNote: 'القائمة كاملة، مستوى مستوى، وما يتطلبه كل واحد. سيفتح المسار، ومعه يبدأ تشغيل الجهاز.',
    showAllWorlds: (n: number) => `اعرض كل الفصول (${num(n, 'ar')})`,
    tryLabel: 'جرّب هنا',
    tryTitle: 'شغّل أول أمر لك',
    tryLede: 'في المستوى 0 تقترح Iris الأوامر، وأنت تقرّر ما يُشغَّل. جرّب أمرًا هنا أولًا: بلا تنزيل وبلا حساب. اكتبه واضغط Enter.',
    tryAfter: 'هذا ما تعرضه الـ terminal في المستوى 0، حيث تقترح Iris الأوامر التالية وأنت تقرّر. أما المستوى نفسه فيعمل على بيئة حقيقية داخل هذه الصفحة.',
    fullCourse: 'المسار الكامل',
    levelCount: (n: number) => `${num(n, 'ar')} ${plural(n, 'مستويات', 'مستوى')}`,
    minutes: (n: number) => `≈ ${num(n, 'ar')} دقيقة`,
    signedInAs: (name: string) => `أنت مسجَّل الدخول باسم ${name}.`,
    resumeAt: (level: number) => `توقّفت عند المستوى ${num(level, 'ar')}.`,
    resumeAny: 'لديك تقدّم محفوظ في هذا المتصفّح.',
    toCourse: 'افتح مسارك',
    open: 'افتح المسار',
  },

  /* --- Where you were ----------------------------------------------------- */
  upnext: {
    label: 'التالي',
    place: (chapter: string, level: number) => `${chapter} · المستوى ${num(level, 'ar')}`,
  },

  /* English keys — they are the join to the missions — with Arabic names.
     The names are fixed course-wide: the same chapter is referred to by name
     inside mission prose and warm-up cards, so these are not free to vary. */
  worlds: {
    'The Workstation': {
      name: 'محطة العمل',
      tagline: 'الجهاز الذي بين يديك. أين أنت، وما الموجود، وكيف تقرأه.',
    },
    'First Commits': {
      name: `أول ${ltr('commits')} لك في Git`,
      tagline: 'من هنا تبدأ مع Git: تحوّل مجلدًا إلى سجل للتغييرات وتختار ما يدخل فيه.',
    },
    'Real Tasks': {
      name: 'مهام حقيقية',
      tagline: 'عمل حقيقي لوكيلك: اختبارات فاشلة، وتعطّل، وخلل مخفي في السجل، في شيفرة أكبر من أن تُقرأ كلها.',
    },
    'Branches & Remotes': {
      name: 'الفروع والمستودعات البعيدة',
      tagline: `العمل مع الآخرين: ${ltr('push')} و ${ltr('pull')}، ثم ${ltr('merge conflict')} لا مفر منه.`,
    },
    Recovery: {
      name: 'الاستعادة',
      tagline: 'استعد ما ظننته ضاع باستخدام reflog و rebase، وانتقل بين المهام من دون فقد عملك.',
    },
    Quality: {
      name: 'الجودة',
      tagline: 'استخدم الاختبارات خريطةً، وتتبع الاستدعاءات دليلًا، وأثبت أن الخلل لن يعود.',
    },
    'Team Git': {
      name: 'Git في الفريق',
      tagline: 'استخدم cherry-pick و bisect و squash، وأنجز تذكرة كاملة بنفسك.',
    },
    'Code Review': {
      name: 'مراجعة الشيفرة',
      tagline: 'الفرق الذي سيقرؤه غيرك، والمراجعة التي عليك الرد عليها.',
    },
    'The Codebase': {
      name: 'قاعدة الشيفرة',
      tagline: 'تعامل مع شيفرة قديمة لا يتذكرها أحد: تتبعها بـ blame، وأعد تسميتها، واحذف غير المستخدم منها، وقسّمها.',
    },
    Releases: {
      name: 'الإصدارات',
      tagline: 'حوّل التغييرات إلى إصدار يصل إلى العملاء: وسوم، وملاحظات إصدار، وإصلاحات عاجلة، وخطط تراجع.',
    },
    Production: {
      name: 'الإنتاج',
      tagline: 'تنبيه في الثانية صباحًا. اقرأ السجلات، وأعد إنتاج الخلل، وأوقف النزيف، ثم اكتب ما جرى.',
    },
    'The Pipeline': {
      name: ltr('CI pipeline'),
      tagline: `اجمع الفحوص في أمر واحد، ثم شغّله تلقائيًا عبر ${ltr('Git hook')} و CI.`,
    },
    Data: {
      name: 'البيانات',
      tagline: 'التغييرات التي لا يكفي فيها التراجع: رحّل البيانات إلى الأمام، واجعل الترحيل آمنًا عند التكرار، ولا تفقد شيئًا.',
    },
    Design: {
      name: 'التصميم',
      tagline: `غيّر الأنظمة الحساسة بأمان باستخدام ${ltr('feature flag')}، وعقد ثابت، وقياس حقيقي.`,
    },
    Ownership: {
      name: 'تحمّل المسؤولية',
      tagline: `أصبحت مسؤولًا عن الخدمة: قيّم ${ltr('dependencies')}، واكتب ${ltr('runbook')}، وتولّ المناوبة الليلية.`,
    },
    'The Platform': {
      name: 'المنصّة',
      tagline: `أكثر ما تحتاج تغييره ليس لك: أنشئ fork، وافتح pull request، والقَ فرعًا محميًّا يرفض.`,
    },
    'The Program': {
      name: 'البرنامج',
      tagline: `الملف ليس عملية. ماذا يصير البرنامج لحظة تشغيله، ولماذا لا يصل إليه تعديل الملف بعدها.`,
    },
    'The Network': {
      name: 'الشبكة',
      tagline: `اقرأ ما على السلك، واجعل سطر الحالة يعني ما يقول، وضع مهلة على كل نداء يغادر.`,
    },
    'The Database': {
      name: 'قاعدة البيانات',
      tagline: `اسأل البيانات نفسها، واقرأ الخطّة قبل إضافة فهرس، وقل متى تكون كتابتان حقيقةً واحدة.`,
    },
    'The API': {
      name: 'الواجهة البرمجية',
      tagline: `صمّم لمن لا تستطيع النشر نيابةً عنهم: عناوين، ونسخ، ومجموعات محدودة، وأخطاء يقرؤها برنامج.`,
    },
    'Security': {
      name: 'الأمان',
      tagline: `أبطِل قبل أن تنظّف، ولا تدع قيمةً تصير جملة، واسأل هل يجوز له كما تسأل من هذا.`,
    },
    'Concurrency': {
      name: 'التزامن',
      tagline: `اقرأ وقرّر واكتب ثلاثة أشياء؛ اجعلها واحدًا، وتوقّع أن تصل كل مهمّة مرّتين.`,
    },
    'Observability': {
      name: 'القابليّة للرصد',
      tagline: `المتوسّط عن الجميع والشكوى عن شخص؛ جد الفجوة التي لا يملكها أحد، ولا تُوقظ إلا لما يحتاج إنسانًا الآن.`,
    },
    'Scale & Cost': {
      name: 'الحجم والكلفة',
      tagline: `مفتاح الذاكرة الوسيطة ادّعاء، وواحد في المرّة ليس حملًا، والفاتورة حسابٌ على أحد أن يجريه.`,
    },
    'Legacy': {
      name: 'الشيفرة الموروثة',
      tagline: `صِفه قبل أن تغيّره، وثبّت الأخطاء أيضًا، وضع الوصلة أوّلًا، وتدرّج بشيء لا يستطيع تغيير رأيه.`,
    },
    'Configuration': {
      name: 'الإعداد',
      tagline: `قطعة واحدة في كل مكان، والبيئة تخبرها أين هي، وبناءٌ تستطيع إعادة إنتاجه هو السبيل الوحيد لمعرفة ما يعمل.`,
    },
    'The Craft': {
      name: 'الصنعة',
      tagline: `اقطع حيث تختلف أسباب التغيير، واكتب الحلقة ثلاث مرّات، وضع الوحدة في الاسم، ورتّب الدين بما كلّفه.`,
    },
    'The Engineer': {
      name: 'المهندس',
      tagline: `قدّر من السجلّ، وجد السؤال خلف الطلب، واحفظ الجواب حيث يجده من يأتي بعدك، وقل لا بإصلاح الشيء الحقيقيّ.`,
    },
    'The Whole Job': {
      name: 'العمل كلّه',
      tagline: `خفّف قبل أن تفهم وارفعه بعدها، وأطلقها مطفأةً مع طريقة لرؤيتها تعمل، واترك نظامًا يكون بخير من دونك.`,
    },
    /* فصول مسار الأمان الأربعة، ولاحقًا المحطات. كانت غائبة عن هذا الجدول،
       وغيابها لا يُرى في الإنجليزية لأن الشيفرة تعود إلى المفتاح نفسه، بينما
       يُرى في العربية اسمَ فصل إنجليزيًّا داخل صفحة عربية (docs/i18n.md §6.1).
       `محطة` هي كلمة الدورة للـ checkpoint، وهي ما تقوله عناوين المحطات
       وسياقاتها العربية أصلًا. */
    Checkpoints: {
      name: 'المحطات',
      tagline: `محطة واحدة لكل فصل: مسألة بلا تلميحات وبلا شرح، والفصل كله هو التحضير لها.`,
    },
    'Trust the input': {
      name: 'الثقة بالمُدخَل',
      tagline: `كل ما يأتي من الخارج دعوى. سمِّ ما يمكن أن يسوء، وامنع قيمةً من أن تصير جملة.`,
    },
    'Who you are, and who says so': {
      name: 'من أنت، ومن يشهد بذلك',
      tagline: `الهوية سؤالان لا سؤال واحد: من هذا، وهل يجوز له. الجلسات، والمعرّفات في الروابط، وما يفشيه نموذج الدخول.`,
    },
    'Blast radius': {
      name: 'نطاق الأثر',
      tagline: `افترض أنه تسرّب: ضيّق صلاحية الرمز، وسمِّ الـ tenant في كل استعلام، واستطع أن تقول من فعلها.`,
    },
    'Working securely with other people': {
      name: 'العمل الآمن مع الآخرين',
      tagline: `اقرأ الفرق بحثًا عمّا يتيحه لأحدهم، وعُدّ الـ dependency والفاتورة ورسالةَ غريبٍ جزءًا من النظام.`,
    },
    /* فصول مسار هندسة الذكاء الاصطناعي الأربعة (docs/ai-track.md). */
    "The agent's pull request": {
      name: 'الـ pull request الذي فتحه الوكيل',
      tagline: `أخضر وواثق ومخطئ. طابِق كل ادّعاء على الفرق، واجعل الخطأ نفسه مستحيلَ الدمج مرةً ثانية.`,
    },
    'Calling the model': {
      name: 'مخاطبة النموذج',
      tagline: `الـ 200 ليست إجابة. تحقّق من الشكل، واقرأ لماذا توقّف، وادفع ثمن البادئة مرةً واحدة، وقِس قبل أن تصدّق.`,
    },
    'Giving it tools': {
      name: 'منحه الأدوات',
      tagline: `شغّل الحلقة، وأبقِ الصلاحية في الشيفرة، وعامِل ما قرأه بيانات، وأوقفه قبل أن توقفه الفاتورة.`,
    },
    'Answers from our own documents': {
      name: 'إجابات من وثائقنا',
      tagline: `استرجِع الصفحة الصحيحة، واذكر مصدرها أو قل إنك لا تعرف، وثبّت النموذج، وتحمّل الحادثة حين يسوء الأمر رغم ذلك.`,
    },
  },
};

const levelsDe = (n: number) => `${num(n, 'de')} Level`;

/**
 * German. `offer.points` says which levels are German and which are still
 * English: German launched part-written (PARTIAL_LOCALES, packages/engine/src/
 * i18n.ts), and a German reader who pays should know what they are buying
 * rather than infer it from the interface's language. Change that line as the
 * translation moves, and drop the English half on the day it is done.
 */
export const de: typeof en = {
  irisIntro: {
    role: 'Deine entworfene KI-Agentin', meet: 'Lerne Iris kennen',
    greeting: 'Hi, ich bin Iris. Du lenkst, ich tippe.',
    artAlt: 'Iris, ein Roboter in Graphit und Elfenbein mit einem grünen Auge, begrüßt dich.',
    pause: 'Animation pausieren', play: 'Animation abspielen',
    title: 'Du lenkst. Iris tippt.',
    body: 'Iris schlägt jeden Befehl vor, und du entscheidest: freigeben, ablehnen oder ihr sagen, was sie stattdessen tun soll. Sie ist so entworfen, dass sie manchmal falsch liegt – die Fähigkeit, die du aufbaust, ist, die Arbeit eines Agenten zu beurteilen.',
    principle: 'Lies, was sie vorschlägt, frag nach dem Warum, wenn es unklar ist, und gib nie einen Befehl frei, den du nicht erklären könntest.',
    cta: 'Jetzt loslegen', preview: 'Beispiel', choose: 'Iris lenken ausprobieren',
    access: (through) => `In den Levels 0 bis ${num(through, 'de')} tippt Iris; du lenkst sie und entscheidest, was sie ausführt.`,
    note: (_agentFrom: number) => `Beispiele. Iris schlägt ab dem ersten Level Befehle vor, die du vor dem Ausführen freigibst, in den ersten Kapiteln und an jedem Checkpoint. Wie jeder Agent liegt sie manchmal falsch, also übst du, ihre Arbeit zu prüfen.`,
    examples: [
      {label:'Auftrag geben',question:'Die Tests schlagen auf main fehl. Finde heraus, warum, aber ändere noch nichts.',answer:'Ich schlage vor, npm test auszuführen und den ersten Fehler zu lesen. Das liest nur, es ändert sich nichts. Freigeben?',takeaway:'Nenne Ziel und Grenzen; sie schlägt den ersten Schritt vor.'},
      {label:'Freigeben oder ablehnen',question:'Du willst einen Force-Push machen. Warum?',answer:'Um den entfernten Branch durch unseren zu ersetzen. Gut, dass du mich stoppst: Priyas Commits gingen verloren. Ich mache stattdessen fetch und rebase.',takeaway:'Lehne ab, was du nicht unterschreiben würdest, und sag, was stattdessen passieren soll.'},
      {label:'Ihre Arbeit prüfen',question:'Du sagst, der Fix ist fertig. Zeig es mir.',answer:'Der Diff ändert eine Datei, und npm test besteht. Einen Test für die leere Eingabe habe ich nicht hinzugefügt. Soll ich?',takeaway:'Fertig heißt belegt: Diff lesen, Prüfungen laufen lassen.'},
    ],
  },
  bar: {
    notSynced: 'nicht synchronisiert',
    notSyncedWhy: 'Die letzte Änderung hat dein Konto nicht erreicht. Es wird erneut versucht.',
    restoreCode: 'Hast du einen Speichercode?',
    restoreCodeWhy: 'Füg einen früher gespeicherten Code ein, dann kommen seine Level in diesen Browser',
  },
  hero: {
    title: (br) => ['Arbeite mit KI-Agenten.', br, 'Erledige den Job.'],
    ctaStart: (level) => `Starten · Level ${num(level, 'de')}`,
    ctaContinue: (level) => `Weiter · Level ${num(level, 'de')}`,
    ctaUnlock: (from, to) => `Level ${num(from, 'de')}–${num(to, 'de')} freischalten`,
    ctaUnlockCount: (count) => (count === 1 ? 'Das letzte Level freischalten' : `Die übrigen ${levelsDe(count)} freischalten`),
    ctaReplay: 'Alle Level geschafft · Nochmal spielen',
    ctaIdle: 'Starten',
    noteNext: (level, minutes) => `Level ${num(level, 'de')} dauert etwa ${num(minutes, 'de')} Minuten.`,
    noteUnlock: (owned, from, to) =>
      `Alle ${levelsDe(owned)}, die dir gehören, sind geschafft. Level ${num(from, 'de')}–${num(to, 'de')} sind der Rest des Kurses.`,
    noteUnlockCount: (owned, count) =>
      `Alle ${levelsDe(owned)}, die dir gehören, sind geschafft. Im vollen Kurs ${count === 1 ? 'gibt es noch eins' : `gibt es noch ${num(count, 'de')}`}.`,
    noteReplay: 'Alle Level geschafft. Spiel jedes davon noch einmal für ein besseres Ergebnis.',
    noteIdle: 'Level werden geladen.',
  },
  offer: {
    label: 'Der Rest des Kurses',
    title: (from, to) => `Level ${num(from, 'de')}–${num(to, 'de')}`,
    titleCount: (count) => (count === 1 ? 'Ein weiteres Level' : `${num(count, 'de')} weitere Level`),
    lede: (levels, free) =>
      `Die ersten ${levelsDe(free)} bringen dir die Werkzeuge bei. Diese ${levelsDe(levels)} sind der Job: eine Codebasis, die du nicht geschrieben hast, eine Pipeline, die kaputtgeht, ein Review, das dir widerspricht, und ein Vorfall zur falschen Stunde – und danach der Pfad KI-Engineering, in dem du die Pull Requests eines Coding-Agenten reviewst und auf einem Modell aufbaust.`,
    points: [
      'Jeder Auftrag, jede Hinweisleiter und jede Nachbesprechung im bezahlten Teil, der ganze Pfad KI-Engineering inklusive.',
        'Die Level 0–20 samt Nachbesprechungen und ihren Kapitel-Einführungen, der Pfad KI-Engineering und alle Checkpoints sind auf Deutsch. Die übrigen Level sind vorerst auf Englisch. Alles, was du schon geschafft hast, bleibt geschafft.',
      'Drei Monate von allem für eine Zahlung, oder ein Monat zum Anfangen. Nichts verlängert sich von selbst: Was danach kommt, entscheidest du.',
      'Zertifikate sind inbegriffen.',
    ],
    price: (amount, currency) => money(amount, currency, 'de'),
    priceWhy: 'Für drei Monate. Nichts verlängert sich von selbst.',
    charged: (amount) => `Du zahlst ${amount}; deine Bank rechnet zu ihrem eigenen Kurs um.`,
    regularLabel: '(regulärer Preis)',
    regional: (country) => `Regionaler Preis für ${country}.`,
    sale: (percent, date) => `${num(percent, 'de')} % Rabatt bis ${date}`,
    buy: 'Den vollen Kurs holen',
    monthLabel: 'Oder ein Monat',
    monthWhy: (percent) => `Drei Monate kosten ${num(percent, 'de')} % weniger pro Monat.`,
    monthBuy: 'Einen Monat holen',
    buyWhy: 'Öffnet Stripe – Karte, Apple Pay oder Google Pay',
    signedOut: 'Du meldest dich zuerst an: Der Kurs wird für ein Konto freigeschaltet, nicht für einen Browser.',
    off: 'Kartenzahlungen sind hier noch nicht eingeschaltet.',
    haveCode: 'Ich habe einen Einladungscode',
    noCard: 'Keine Karte, oder eine, die nicht durchgeht? Sag uns Bescheid, dann finden wir einen Zahlungsweg, der bei dir funktioniert.',
    noCardLink: 'Support fragen',
  },
  term: {
    until: (date) => `Dein Zugang zum vollen Kurs läuft bis zum ${date}.`,
    renewSoon: (date) =>
      `Dein Zugang zum vollen Kurs endet am ${date}. Wenn du jetzt verlängerst, zählt die Verlängerung ab diesem Tag – früh zu verlängern kostet dich also nichts.`,
    ended: (date) =>
      `Dein Zugang zum vollen Kurs endete am ${date}. Alles, was du geschafft hast, deine Zertifikate und deine öffentliche Seite gehören weiter dir.`,
    renew: 'Um drei Monate verlängern',
    renewMonth: 'Um einen Monat verlängern',
  },
  invite: {
    label: 'Einladungscode',
    title: 'Mit einem Code freischalten',
    lede: 'Ein Code schaltet genau das frei, was eine Zahlung freischaltet: jedes Level im bezahlten Teil, für das Konto, mit dem du angemeldet bist.',
    field: 'Dein Code',
    placeholder: 'EOS-XXXX-XXXX-XXXX-…',
    redeem: 'Kurs freischalten',
    redeeming: 'Wird geprüft …',
    close: 'Schließen',
    closeWhy: 'Nichts wird eingelöst, bevor du auf „Kurs freischalten“ drückst',
    signIn: 'Anmelden',
    signedOut: 'Melde dich zuerst an, damit es ein Konto zum Freischalten gibt.',
    said: {
      redeemed: (from, to) => `Freigeschaltet. Level ${num(from, 'de')}–${num(to, 'de')} sind jetzt auf der Karte.`,
      redeemedCount: (count) =>
        `Freigeschaltet. ${count === 1 ? 'Ein Level ist' : `${num(count, 'de')} Level sind`} jetzt auf der Karte.`,
      redeemedAll: 'Freigeschaltet. Jedes Level des Kurses ist jetzt auf der Karte.',
      already: 'Dieses Konto hat den vollen Kurs schon. Es wurde nichts geändert.',
      malformed: 'Das ist kein vollständiger Code. Nach den Bindestrichen stehen 52 Zeichen – prüf, ob eins beim Kopieren gefehlt hat.',
      unknown: 'Dieser Code ist nicht gültig.',
      spent: 'Dieser Code wurde schon benutzt. Einmalige Codes funktionieren einmal – frag nach einem neuen.',
      anonymous: 'Melde dich zuerst an, damit es ein Konto zum Freischalten gibt.',
      unavailable: 'Einladungscodes sind hier nicht eingeschaltet.',
      failed: 'Der Code konnte nicht geprüft werden. Versuch es gleich noch einmal.',
    },
    privacy: 'Ein Code ist kein Passwort. Er schaltet den Kurs für dieses Konto frei und tut sonst nichts.',
  },
  standing: {
    label: 'Dein Stand',
    rank: 'Rang',
    rankMeter: 'Fortschritt zum nächsten Rang',
    toNextRank: (xp, rank) => `${num(xp, 'de')} XP bis ${rank}`,
    topRank: 'Höchster Rang erreicht',
    xp: 'XP',
    levels: 'Level',
    stars: 'Sterne',
    outOf: (done, total) => [done, `/${num(total, 'de')}`],
    localOnly: 'Nur in diesem Browser gespeichert',
    localOnlyWhy: 'Wenn die Daten dieser Website gelöscht werden, verschwindet es mit allem anderen.',
    signIn: 'Anmelden',
    signingIn: 'Anmeldung läuft …',
  },
  ledger: {
    title: 'Der Kurs',
    note: 'Geschaffte Welten sind eingeklappt. Drei Sterne ohne Hinweise, zwei mit bis zu drei, einer nach der Lösung. Nimm in einem Level jedes Werkzeug, das du willst, KI eingeschlossen: Bewertet wird, was du im Repository hinterlässt.',
  },
  world: {
    count: (cleared, total) => `${num(cleared, 'de')} / ${num(total, 'de')}`,
    starCount: (stars) => ` · ${num(stars, 'de')}★`,
    meterLabel: (world, cleared, total) => `${world}: ${num(cleared, 'de')} von ${num(total, 'de')} geschafft`,
    checkpointWaiting: (world) => `${world}: Der Checkpoint für dieses Kapitel ist offen und noch nicht bestanden`,
    openerLabel: 'Hier anfangen',
    openerMinutes: (minutes) => `${num(minutes, 'de')} Min.`,
  },
  row: {
    label: (level, title) => `Level ${num(level, 'de')}: ${title}`,
    labelPaid: (level, title) => `Level ${num(level, 'de')}: ${title} – Teil des vollen Kurses`,
    labelCheckpoint: (title) => `${title}: der Checkpoint für dieses Kapitel`,
    labelCheckpointPaid: (title) => `${title}: der Checkpoint für dieses Kapitel – Teil des vollen Kurses`,
    unverified: 'unbestätigt',
    unverifiedWhy: (why) => `Dieser Abschluss konnte nicht bestätigt werden${why ? `: ${why}` : ''}. Spiel das Level noch einmal, um ihn zu bestätigen.`,
    paid: 'voller Kurs',
    paidWhy: (from, to) => `Teil des vollen Kurses. Schalte Level ${num(from, 'de')}–${num(to, 'de')} frei, um es zu spielen.`,
    paidWhyCount: (count) =>
      `Teil des vollen Kurses. Das Freischalten öffnet ${count === 1 ? 'ein Level' : levelsDe(count)}, dieses eingeschlossen.`,
    skipped: 'übersprungen',
    skippedWhy: 'Das hast du übersprungen. Es ist noch da, und seine Sterne warten.',
    minutes: (minutes) => `${num(minutes, 'de')} Min.`,
    xpEarned: (xp) => `${num(xp, 'de')} XP`,
    xpOffered: (xp) => `+${num(xp, 'de')} XP`,
    debrief: (hidden) => ['Nachbesprechung', hidden],
    debriefFor: (level, title) => ` zu Level ${num(level, 'de')}, ${title}`,
    debriefForCheckpoint: (title) => ` zu ${title}`,
  },
  footer: {
    fiction: 'Lattice, Priya und Marcus sind erfunden. Das Repository, die Shell und git sind echt. Iris und Kite sind entworfene Agenten. Blobor ist unser eigenes Spiel.',
    unverifiedNote: 'Hohle Sterne sind Abschlüsse, die nicht bestätigt werden konnten. Spiel das Level noch einmal, um sie zu füllen.',
    starsNote: 'Drei Sterne ohne Hinweise. Zwei mit bis zu drei Hinweisen. Einer nach der Lösung.',
    legal: 'AGB',
    privacy: 'Datenschutz',
    refunds: 'Erstattungen',
    about: 'Über EngineerOS',
  },
  landing: {
    game: {
      label: 'Wohin die Übung führt',
      title: 'Das ist Blobor, unser Spiel.',
      lede: 'Wir entwickeln auch Blobor, ein Multiplayer-Spiel in Luma. Mit den Fähigkeiten, die du mit Iris übst, kannst du uns beim Testen, bei der Fehlersuche und beim Entwickeln kleiner Teile helfen.',
      play: 'Blobor spielen',
      artSource: 'Aus dem Trailer zur Geschichte von Luma',
      artAlt: 'Ein grüner Blobor auf einem steinernen Wachturm blickt bei Sonnenuntergang über Lumas Inseln und Häfen.',
    },
    theCourse: 'Der Kurs',
    ageLabel: 'Warum Agenten',
    ageTitle: 'Warum KI-Agenten lenken?',
    whyLabel: 'Warum EngineerOS',
    whyTitle: 'Warum mit EngineerOS lernen?',
    whyWatch: 'Video: Warum Engineers jetzt Agenten lenken',
    ctaNote: 'Kein Konto nötig. Das erste Level läuft in diesem Tab.',
    pricing: 'Preise',
    courseTitle: 'Der Kurs',
    courseNote: 'Jedes Level ist eine Aufgabe in einem Repository. Das sind die Kapitel, der Reihe nach: von deinem ersten Commit bis zum Review dessen, was ein Coding-Agent öffnet.',
    allLevels: (n) => `Alle ${levelsDe(n)} ansehen`,
    allLevelsNote: 'Die ganze Liste, Level für Level, mit dem, was jedes braucht. Sie öffnet den Kurs, also startet auch die Maschine.',
    showAllWorlds: (n) => `Alle ${num(n, 'de')} Kapitel zeigen`,
    tryLabel: 'Hier ausprobieren',
    tryTitle: 'Führ deinen ersten Befehl aus',
    tryLede: 'In Level 0 schlägt Iris die Befehle vor, und du entscheidest, was läuft. Probier hier zuerst einen aus: ohne Download, ohne Konto. Tipp ihn ein und drück Enter.',
    tryAfter: 'Das zeigt das Terminal in Level 0, wo Iris die nächsten Befehle vorschlägt und du entscheidest. Das Level selbst läuft auf einer echten Maschine in diesem Tab.',
    fullCourse: 'Voller Kurs',
    levelCount: (n) => levelsDe(n),
    minutes: (n) => `≈ ${num(n, 'de')} Min.`,
    signedInAs: (name) => `Angemeldet als ${name}.`,
    resumeAt: (level) => `Du warst zuletzt bei Level ${num(level, 'de')}.`,
    resumeAny: 'In diesem Browser ist Fortschritt gespeichert.',
    toCourse: 'Deinen Kurs öffnen',
    open: 'Den Kurs öffnen',
  },
  upnext: {
    label: 'Als Nächstes',
    place: (chapter, level) => `${chapter} · Level ${num(level, 'de')}`,
  },
  worlds: {
    'The Workstation': {
      name: 'Der Arbeitsplatz',
      tagline: 'Die Maschine unter deinen Händen. Wo du bist, was da ist und wie man es liest.',
    },
    'First Commits': {
      name: 'Erste Commits',
      tagline: 'Wo git anfängt. Aus einem Ordner wird eine Historie, und du entscheidest, was hineinkommt.',
    },
    'Real Tasks': {
      name: 'Echte Aufgaben',
      tagline: 'Echte Arbeit für deinen Agenten: eine rote Testsuite, ein Absturz, ein Fehler in der Historie, in einer Codebasis, die zu groß ist, um sie ganz zu lesen.',
    },
    'Branches & Remotes': {
      name: 'Branches & Remotes',
      tagline: 'Es gibt andere Menschen. Push, Pull und der Konflikt, vor dem sich alle fürchten.',
    },
    Recovery: {
      name: 'Wiederherstellung',
      tagline: 'Nichts ist verloren. Reflog, Rebase und die Arbeit wechseln, ohne sie fallen zu lassen.',
    },
    Quality: {
      name: 'Qualität',
      tagline: 'Tests als Karte, Stack Traces als Wegbeschreibung und der Beweis, dass Bugs tot bleiben.',
    },
    'Team Git': {
      name: 'Git im Team',
      tagline: 'Cherry-Pick, Bisect, Squash – und ein ganzes Ticket allein ausliefern.',
    },
    'Code Review': {
      name: 'Code-Review',
      tagline: 'Der Diff, den jemand anderes lesen muss, und das Review, auf das du antworten musst.',
    },
    'The Codebase': {
      name: 'Die Codebasis',
      tagline: 'Code, an dessen Entstehung sich niemand erinnert. Blame, umbenennen, löschen, aufteilen.',
    },
    Releases: {
      name: 'Releases',
      tagline: 'Der Moment, in dem Änderungen etwas werden, das Kunden haben. Tags, Notes, Hotfixes, Rollbacks.',
    },
    Production: {
      name: 'Produktion',
      tagline: 'Ein Alarm um zwei Uhr nachts. Logs lesen, nachstellen, die Blutung stoppen, aufschreiben.',
    },
    'The Pipeline': {
      name: 'Die Pipeline',
      tagline: 'Ein Befehl, der prüft, ein Hook, der ihn ausführt, und CI, die ihn ohne dich ausführt.',
    },
    Data: {
      name: 'Daten',
      tagline: 'Der Teil, den du nicht rückgängig machen kannst. Vorwärts migrieren, einen zweiten Lauf überstehen, nichts verlieren.',
    },
    Design: {
      name: 'Design',
      tagline: 'Die riskanten Änderungen: ein Flag, ein Vertrag, auf den sich jemand verlässt, eine Messung.',
    },
    Ownership: {
      name: 'Verantwortung',
      tagline: 'Der Dienst gehört jetzt dir. Sag Nein zu einer Abhängigkeit, schreib das Runbook, übernimm die Nacht.',
    },
    'The Platform': {
      name: 'Die Plattform',
      tagline: 'Das meiste, was du ändern musst, gehört jemand anderem. Forken, den Request öffnen und auf einen Branch treffen, der Nein sagt.',
    },
    'The Program': {
      name: 'Das Programm',
      tagline: 'Eine Datei ist kein Prozess. Was ein Programm in dem Moment wird, in dem es läuft, und warum eine Änderung an der Datei danach nichts mehr erreicht.',
    },
    'The Network': {
      name: 'Das Netzwerk',
      tagline: 'Lies, was auf der Leitung ist, meine die Statuszeile ernst und gib allem, was hinausgeht, eine Frist.',
    },
    'The Database': {
      name: 'Die Datenbank',
      tagline: 'Frag die Daten selbst, lies den Plan, bevor du einen Index anlegst, und sag, wann zwei Schreibvorgänge eine Tatsache sind.',
    },
    'The API': {
      name: 'Die API',
      tagline: 'Entwirf für Leute, für die du nicht deployen kannst: Adressen, Versionen, begrenzte Sammlungen, Fehler, die ein Programm lesen kann.',
    },
    Security: {
      name: 'Sicherheit',
      tagline: 'Erst widerrufen, dann aufräumen, nie einen Wert zum Satz werden lassen, und nicht nur fragen, wer, sondern auch, ob jemand darf.',
    },
    Concurrency: {
      name: 'Nebenläufigkeit',
      tagline: 'Lesen, entscheiden und schreiben sind drei Dinge; mach eins daraus und rechne damit, dass jeder Job zweimal ankommt.',
    },
    Observability: {
      name: 'Beobachtbarkeit',
      tagline: 'Ein Mittelwert handelt von allen, eine Beschwerde von jemandem; finde die Lücke, für die niemand zuständig ist, und alarmiere nur für das, was jetzt einen Menschen braucht.',
    },
    'Scale & Cost': {
      name: 'Skalierung und Kosten',
      tagline: 'Ein Cache-Schlüssel ist eine Behauptung, eine Anfrage nach der anderen ist keine Last, und die Rechnung ist Arithmetik, die jemand machen muss.',
    },
    Legacy: {
      name: 'Altsysteme',
      tagline: 'Beschreib es, bevor du es änderst, halte auch die Fehler fest, setz zuerst die Nahtstelle und fahr es nach etwas hoch, das seine Meinung nicht ändern kann.',
    },
    Configuration: {
      name: 'Konfiguration',
      tagline: 'Ein Artefakt überall, die Umgebung sagt ihm, wo es ist, und ein reproduzierbarer Build ist der einzige Weg zu wissen, was läuft.',
    },
    'The Craft': {
      name: 'Das Handwerk',
      tagline: 'Schneide dort, wo die Gründe für Änderungen auseinandergehen, schreib die Schleife dreimal, pack die Einheit in den Namen und ordne Schulden danach, was sie gekostet haben.',
    },
    'The Engineer': {
      name: 'Der Engineer',
      tagline: 'Schätze anhand des Logs, finde die Frage hinter der Bitte, leg die Antwort dort ab, wo die nächste Person sie findet, und sag Nein, indem du das Eigentliche reparierst.',
    },
    'The Whole Job': {
      name: 'Der ganze Job',
      tagline: 'Erst eindämmen, dann verstehen und danach wieder aufheben, mit einem Weg ausliefern, es arbeiten zu sehen, und ein System hinterlassen, das ohne dich zurechtkommt.',
    },
    Checkpoints: {
      name: 'Checkpoints',
      tagline: 'Eine Aufgabe pro Kapitel, ohne Hinweise und ohne Anleitung. Das Kapitel ist die Vorbereitung.',
    },
    'Trust the input': {
      name: 'Der Eingabe trauen',
      tagline: 'Alles von außen ist eine Behauptung. Benenne, was schiefgehen kann, und verhindere, dass ein Wert zum Satz wird.',
    },
    'Who you are, and who says so': {
      name: 'Wer du bist, und wer das sagt',
      tagline: 'Identität sind zwei Fragen, nicht eine: Wer ist das, und darf die Person das? Sessions, IDs in URLs und was ein Login-Formular verrät.',
    },
    'Blast radius': {
      name: 'Schadensradius',
      tagline: 'Geh davon aus, dass es rauskommt. Begrenze den Token, nenne den Mandanten in jeder Abfrage und sorg dafür, dass du sagen kannst, wer das war.',
    },
    'Working securely with other people': {
      name: 'Sicher mit anderen arbeiten',
      tagline: 'Lies einen Diff darauf, was er jemandem erlaubt, und zähl eine Abhängigkeit, eine Rechnung und die E-Mail einer fremden Person zum System.',
    },
    "The agent's pull request": {
      name: 'Der Pull Request des Agenten',
      tagline: 'Grün, selbstsicher und falsch. Prüf jede Behauptung am Diff und mach den Fehler unmöglich, ein zweites Mal gemergt zu werden.',
    },
    'Calling the model': {
      name: 'Das Modell aufrufen',
      tagline: 'Ein 200 ist keine Antwort. Prüf die Form, lies, warum es aufgehört hat, bezahl den Präfix nur einmal und miss, bevor du glaubst.',
    },
    'Giving it tools': {
      name: 'Ihm Werkzeuge geben',
      tagline: 'Führ die Schleife aus, behalt die Befugnis im Code, behandle, was es gelesen hat, als Daten, und stopp es, bevor die Rechnung es tut.',
    },
    'Answers from our own documents': {
      name: 'Antworten aus unseren eigenen Dokumenten',
      tagline: 'Hol die richtige Seite, zitier sie oder sag, dass du es nicht weißt, fixier das Modell und übernimm den Vorfall, wenn es trotzdem schiefgeht.',
    },
  },
};

export const ja: typeof en = {
  irisIntro: {
    role: '設計されたAIエージェント', meet: 'Irisを知る',
    greeting: 'こんにちは、Irisです。指示はあなた、入力は私。',
    artAlt: 'Irisは、グラファイトとアイボリーの体に緑色の目が1つあるロボットです。あなたを迎えています。',
    pause: 'アニメーションを一時停止', play: 'アニメーションを再生',
    title: '指示するのはあなた。入力するのはIris。',
    body: 'Irisがすべてのコマンドを提案し、決めるのはあなたです。承認するか、却下するか、代わりに何をすべきか伝えます。Irisはときどき間違えるように設計されているので、身につくのはエージェントの仕事を見極める力です。',
    principle: '提案をよく読み、わからなければ理由を尋ね、自分で説明できないコマンドは決して承認しないこと。',
    cta: '作り始める', preview: 'やり取りの例', choose: 'Irisへの指示を見てみる',
    access: (through) => `レベル0〜${num(through, 'ja')}では、入力するのはIrisです。あなたが指示し、何を実行するかを決めます。`,
    note: (_agentFrom: number) => `やり取りの例です。Irisは最初のレベルから、序盤の章とすべてのチェックポイントで、実行前にあなたが承認するコマンドを提案します。どのエージェントとも同じく時々間違えるので、その仕事をレビューする練習になります。`,
    examples: [
      { label: '依頼する', question: 'mainでテストが失敗しています。原因を調べて。ただし、まだ何も変更しないで。', answer: 'npm testを実行して、最初の失敗を読むことを提案します。読むだけなので、何も変わりません。承認しますか？', takeaway: '目的と制約を伝えれば、最初の一歩はIrisが提案する。' },
      { label: '承認か却下か', question: 'force pushしたいの？　なぜ？', answer: 'リモートのブランチを私たちのものに置き換えるためです。止めてくれて正解です。Priyaのコミットが消えてしまいます。代わりにfetchしてrebaseします。', takeaway: '自分が責任を持てないものは却下し、代わりにどうするかを伝える。' },
      { label: '仕事を確かめる', question: '修正は終わったと言うけれど、見せて。', answer: '差分は1ファイルだけで、npm testは通ります。空の入力に対するテストは追加していません。追加しますか？', takeaway: '完了とは根拠のこと。差分を読み、チェックを実行する。' },
    ],
  },
  bar: {
    notSynced: '未同期',
    notSyncedWhy: '最後の変更がアカウントに届いていません。再試行されます。',
    restoreCode: '保存コードをお持ちですか？',
    restoreCodeWhy: '以前保存したコードを貼り付けると、そのレベルがこのブラウザーに加わります',
  },
  hero: {
    title: (br) => ['AIエージェントと働き、', br, '仕事をやり遂げる。'],
    ctaStart: (level) => `開始 · レベル${num(level, 'ja')}`,
    ctaContinue: (level) => `続ける · レベル${num(level, 'ja')}`,
    ctaUnlock: (from, to) => `レベル${num(from, 'ja')}〜${num(to, 'ja')}をアンロック`,
    ctaUnlockCount: (count) => `残り${num(count, 'ja')}レベルをアンロック`,
    ctaReplay: 'すべてのレベルをクリア · もう一度プレイ',
    ctaIdle: '開始',
    noteNext: (level, minutes) => `レベル${num(level, 'ja')}は約${num(minutes, 'ja')}分で終わります。`,
    noteUnlock: (owned, from, to) => `持っている${num(owned, 'ja')}レベルはすべてクリアしました。レベル${num(from, 'ja')}〜${num(to, 'ja')}がコースの残りです。`,
    noteUnlockCount: (owned, count) => `持っている${num(owned, 'ja')}レベルはすべてクリアしました。コース全体には、あと${num(count, 'ja')}レベルあります。`,
    noteReplay: 'すべてのレベルをクリアしました。どのレベルも、より良いスコアを目指してもう一度プレイできます。',
    noteIdle: 'レベルを読み込んでいます。',
  },
  offer: {
    label: 'コースの続き',
    title: (from, to) => `レベル${num(from, 'ja')}〜${num(to, 'ja')}`,
    titleCount: (count) => `さらに${num(count, 'ja')}レベル`,
    lede: (levels, free) => `最初の${num(free, 'ja')}レベルで、ツールを学びます。この${num(levels, 'ja')}レベルは、仕事そのものです。自分では書いていないコードベース、壊れるパイプライン、自分と意見が食い違うレビュー、最悪の時間帯に起きるインシデント。そして最後は、AIエンジニアリングのトラックです。コーディングエージェントのプルリクエストをレビューし、モデルの上に構築します。`,
    points: [
      '有料部分のすべてのブリーフ、ヒントの段階、振り返り。AIエンジニアリングのトラック全体も含まれます。',
        '画面とレベル0〜20、その振り返りと章の導入、最初の5つのチェックポイントは日本語です。レベル21以降など、残りの課題は今のところ英語です。すでにクリアしたレベルは、そのままクリア済みです。',
      'コース3か月分のすべてが1回のお支払いで、または1か月から始められます。自動で更新されることはありません。その先は、あなたが決めます。',
      '修了証も含まれます。',
    ],
    price: (amount, currency) => money(amount, currency, 'ja'),
    priceWhy: '3か月分です。自動では更新されません。',
    charged: (amount) => `お支払いは${amount}です。ご利用の銀行が、独自のレートで換算します。`,
    regularLabel: '(通常価格)',
    regional: (country) => `${country}向けの地域価格です。`,
    sale: (percent, date) => `${date}まで${num(percent, 'ja')}%オフ`,
    buy: 'コース全体を購入',
    monthLabel: 'または1か月',
    monthWhy: (percent) => `3か月分なら、1か月あたり${num(percent, 'ja')}%お得です。`,
    monthBuy: '1か月分を購入',
    buyWhy: 'Stripeが開きます(カード、Apple Pay、Google Pay)',
    signedOut: '先にログインしていただきます。コースはブラウザーではなく、アカウントに対してアンロックされます。',
    off: 'カード決済は、まだここでは有効になっていません。',
    haveCode: '招待コードを持っています',
    noCard: 'カードをお持ちでない、または決済が通らない場合は、お知らせください。お住まいの地域で使える支払い方法を、一緒に探します。',
    noCardLink: 'サポートに問い合わせる',
  },
  term: {
    until: (date) => `コース全体のご利用期間は${date}までです。`,
    renewSoon: (date) => `コース全体のご利用期間は${date}に終了します。今更新すると、その日から延長されるので、早めに更新しても損はありません。`,
    ended: (date) => `コース全体のご利用期間は${date}に終了しました。クリアしたレベル、修了証、公開ページは、引き続きあなたのものです。`,
    renew: 'コース3か月分を更新',
    renewMonth: '1か月分を更新',
  },
  invite: {
    label: '招待コード',
    title: 'コードでアンロック',
    lede: 'コードは、お支払いと同じものをアンロックします。ログイン中のアカウントで、有料部分のすべてのレベルが対象です。',
    field: 'コード',
    placeholder: 'EOS-XXXX-XXXX-XXXX-…',
    redeem: 'コースをアンロック',
    redeeming: '確認中…',
    close: '閉じる',
    closeWhy: '「コースをアンロック」を押すまで、何も使用されません',
    signIn: 'ログイン',
    signedOut: '先にログインしてください。アンロックするアカウントが必要です。',
    said: {
      redeemed: (from, to) => `アンロックしました。レベル${num(from, 'ja')}〜${num(to, 'ja')}がマップに表示されています。`,
      redeemedCount: (count) => `アンロックしました。${num(count, 'ja')}レベルがマップに表示されています。`,
      redeemedAll: 'アンロックしました。コースのすべてのレベルがマップに表示されています。',
      already: 'このアカウントには、すでにコース全体があります。何も変更していません。',
      malformed: 'コードが最後まで入力されていません。ダッシュのあとに52文字が続きます。コピーのときに欠けた部分がないか確認してください。',
      unknown: 'このコードは無効です。',
      spent: 'このコードはすでに使用されています。1回限りのコードは、1度しか使えません。新しいコードをリクエストしてください。',
      anonymous: '先にログインしてください。アンロックするアカウントが必要です。',
      unavailable: 'ここでは、招待コードは有効になっていません。',
      failed: 'コードを確認できませんでした。少し待ってから、もう一度お試しください。',
    },
    privacy: 'コードはパスワードではありません。このアカウントでコースをアンロックするだけで、ほかには何もしません。',
  },
  standing: {
    label: '現在の状況',
    rank: 'ランク',
    rankMeter: '次のランクまでの進捗',
    toNextRank: (xp, rank) => `${rank}まであと${num(xp, 'ja')} XP`,
    topRank: '最高ランクに到達',
    xp: 'XP',
    levels: 'レベル',
    stars: '星',
    outOf: (done, total) => [done, `/${num(total, 'ja')}`],
    localOnly: 'このブラウザーにのみ保存',
    localOnlyWhy: 'このサイトのデータを消去すると、ほかのすべてと一緒に消えます。',
    signIn: 'ログイン',
    signingIn: 'ログイン中…',
  },
  ledger: {
    title: 'コース',
    note: 'クリアしたワールドはたたまれます。星3つはヒントなし、星2つはヒント3つまで、星1つは解答を見たあとです。レベルでは、AIを含め好きなツールを使って構いません。採点されるのは、リポジトリに残したものです。',
  },
  world: {
    count: (cleared, total) => `${num(cleared, 'ja')} / ${num(total, 'ja')}`,
    starCount: (stars) => ` · ${num(stars, 'ja')}★`,
    meterLabel: (world, cleared, total) => `${world}: ${num(total, 'ja')}レベル中${num(cleared, 'ja')}レベルをクリア`,
    checkpointWaiting: (world) => `${world}: この章のチェックポイントが開いていますが、まだ合格していません`,
    openerLabel: 'ここから始める',
    openerMinutes: (minutes) => `${num(minutes, 'ja')}分`,
  },
  row: {
    label: (level, title) => `レベル${num(level, 'ja')}: ${title}`,
    labelPaid: (level, title) => `レベル${num(level, 'ja')}: ${title} — コース全体に含まれます`,
    labelCheckpoint: (title) => `${title}: この章のチェックポイント`,
    labelCheckpointPaid: (title) => `${title}: この章のチェックポイント — コース全体に含まれます`,
    unverified: '未検証',
    unverifiedWhy: (why) => `このクリアは検証できませんでした${why ? `: ${why}` : ''}。検証するには、レベルをもう一度プレイしてください。`,
    paid: 'コース全体',
    paidWhy: (from, to) => `コース全体に含まれます。プレイするには、レベル${num(from, 'ja')}〜${num(to, 'ja')}をアンロックしてください。`,
    paidWhyCount: (count) => `コース全体に含まれます。アンロックすると、このレベルを含む${num(count, 'ja')}レベルが開きます。`,
    skipped: 'スキップ',
    skippedWhy: 'このレベルは先へ進みました。星は獲得しないまま、ここに残っています。',
    minutes: (minutes) => `${num(minutes, 'ja')}分`,
    xpEarned: (xp) => `${num(xp, 'ja')} XP`,
    xpOffered: (xp) => `+${num(xp, 'ja')} XP`,
    debrief: (hidden) => ['振り返り', hidden],
    debriefFor: (level, title) => `(レベル${num(level, 'ja')}、${title})`,
    debriefForCheckpoint: (title) => `(${title})`,
  },
  footer: {
    fiction: 'Lattice、Priya、Marcusは架空の存在です。リポジトリ、シェル、gitは本物です。IrisとKiteは設計されたエージェントです。Bloborは私たち自身のゲームです。',
    unverifiedNote: '中が空の星は、検証できなかったクリアです。レベルをもう一度プレイすると、星が埋まります。',
    starsNote: '星3つはヒントなし。星2つはヒント3つまで。星1つは解答を見たあと。',
    legal: '利用規約',
    privacy: 'プライバシー',
    refunds: '返金',
    about: 'EngineerOSについて',
  },
  landing: {
    game: {
      label: '練習の先にあるもの',
      title: '私たちのゲーム、Blobor。',
      lede: '私たちは、Lumaを舞台にしたマルチプレイヤーゲームBloborも開発しています。Irisと練習したスキルは、ゲームのテスト、不具合の発見、小さな機能の開発に活かせます。',
      play: 'Bloborで遊ぶ',
      artSource: 'Lumaの物語のトレーラーより',
      artAlt: '夕暮れの石の見張り塔から、緑のBloborがLumaの島々と港を見渡しています。',
    },
    theCourse: 'コース',
    ageLabel: 'なぜエージェントか',
    ageTitle: 'なぜAIエージェントに指示するのか？',
    whyLabel: 'なぜEngineerOSか',
    whyTitle: 'なぜEngineerOSで学ぶのか？',
    whyWatch: '動画：エンジニアが今エージェントに指示する理由',
    ctaNote: 'アカウントは不要です。最初のレベルは、このタブの中で動きます。',
    pricing: '料金',
    courseTitle: 'コース',
    courseNote: 'どのレベルも、リポジトリの中のタスクです。順番に並んだ章がこちらです。最初のコミットから、コーディングエージェントが出すプルリクエストのレビューまで。',
    allLevels: (n) => `全${num(n, 'ja')}レベルを見る`,
    allLevelsNote: 'レベルごとの全一覧と、それぞれに必要なものを表示します。コースが開くので、マシンも一緒に起動します。',
    showAllWorlds: (n) => `全${num(n, 'ja')}章を表示`,
    tryLabel: 'ここで試す',
    tryTitle: '最初のコマンドを実行しよう',
    tryLede: 'レベル0では、Irisがコマンドを提案し、何を実行するかはあなたが決めます。まずはここで1つ試してみましょう。ダウンロードもアカウントも不要です。入力してEnterを押してください。',
    tryAfter: 'これがレベル0のターミナルに表示される内容です。レベル0では、Irisが次のコマンドを提案し、あなたが決めます。レベル本体は、このタブの中の本物のマシンで動きます。',
    fullCourse: 'コース全体',
    levelCount: (n) => `${num(n, 'ja')}レベル`,
    minutes: (n) => `約${num(n, 'ja')}分`,
    signedInAs: (name) => `${name}としてログイン中。`,
    resumeAt: (level) => `レベル${num(level, 'ja')}の途中まで進んでいます。`,
    resumeAny: 'このブラウザーに、保存された進捗があります。',
    toCourse: 'コースを開く',
    open: 'コースを開く',
  },
  upnext: {
    label: '次はこれ',
    place: (chapter, level) => `${chapter} · レベル${num(level, 'ja')}`,
  },
  worlds: {
    'The Workstation': {
      name: 'ワークステーション',
      tagline: 'あなたの手元にあるマシン。今どこにいて、何があって、どう読むか。',
    },
    'First Commits': {
      name: 'はじめてのコミット',
      tagline: 'gitの始まり。フォルダーが履歴になり、何を入れるかを自分で選びます。',
    },
    'Real Tasks': {
      name: '実務のタスク',
      tagline: 'エージェントに任せる実務。失敗するテスト、クラッシュ、履歴に隠れた不具合を、全部は読みきれない大きなコードベースで。',
    },
    'Branches & Remotes': {
      name: 'ブランチとリモート',
      tagline: '他の人もいる世界。プッシュ、プル、そして誰もが恐れるコンフリクト。',
    },
    Recovery: {
      name: 'リカバリー',
      tagline: '失われるものはありません。Reflog、リベース、そして作業を落とさずに切り替える方法。',
    },
    Quality: {
      name: '品質',
      tagline: '地図としてのテスト、道案内としてのスタックトレース、そしてバグが二度と蘇らない証拠。',
    },
    'Team Git': {
      name: 'チームでのGit',
      tagline: 'チェリーピック、bisect、スカッシュ。そして、チケット1件を自分の力でリリースします。',
    },
    'Code Review': {
      name: 'コードレビュー',
      tagline: '誰かが読まなければならない差分と、あなたが答えなければならないレビュー。',
    },
    'The Codebase': {
      name: 'コードベース',
      tagline: '誰も書いた覚えのないコード。blameし、名前を変え、削除し、分割します。',
    },
    Releases: {
      name: 'リリース',
      tagline: '変更が、顧客の手に渡るものに変わる瞬間。タグ、リリースノート、ホットフィックス、ロールバック。',
    },
    Production: {
      name: '本番環境',
      tagline: '午前2時のアラート。ログを読み、再現し、被害を止め、報告を書きます。',
    },
    'The Pipeline': {
      name: 'パイプライン',
      tagline: '確認するコマンド1つ、それを走らせるフック、そしてあなたがいなくても走らせるCI。',
    },
    Data: {
      name: 'データ',
      tagline: '元に戻せない部分。前向きにマイグレーションし、2回目の実行にも耐え、何も失いません。',
    },
    Design: {
      name: '設計',
      tagline: 'リスクの高い変更。フラグ、他の誰かが依存する契約、そして計測。',
    },
    Ownership: {
      name: 'オーナーシップ',
      tagline: 'サービスはもうあなたのものです。依存関係にノーと言い、ランブックを書き、夜の運用を引き受けます。',
    },
    'The Platform': {
      name: 'プラットフォーム',
      tagline: '変更したいもののほとんどは、他の誰かのものです。フォークし、リクエストを出し、「ノー」と言うブランチに出会います。',
    },
    'The Program': {
      name: 'プログラム',
      tagline: 'ファイルはプロセスではありません。プログラムは実行された瞬間に何になるのか、そしてなぜ、あとからファイルを編集しても何にも届かないのか。',
    },
    'The Network': {
      name: 'ネットワーク',
      tagline: '回線を流れるものを読み、ステータス行の意味を理解し、外へ出るすべてにタイムアウトを設けます。',
    },
    'The Database': {
      name: 'データベース',
      tagline: 'データそのものに問いかけ、インデックスを足す前に実行計画を読み、2つの書き込みが1つの事実だと言い切ります。',
    },
    'The API': {
      name: 'API',
      tagline: 'デプロイしてあげられない相手のための設計。アドレス、バージョン、上限のあるコレクション、プログラムが読めるエラー。',
    },
    'Security': {
      name: 'セキュリティ',
      tagline: '片付ける前に無効化し、値を文として解釈させず、「誰か」だけでなく「許されているか」も問います。',
    },
    'Concurrency': {
      name: '並行処理',
      tagline: '読み取り、判断、書き込みは3つの操作です。それを1つにし、どのジョブも2回届くものと考えます。',
    },
    'Observability': {
      name: 'オブザーバビリティ',
      tagline: '平均はみんなについての数字で、苦情は誰かについての声です。誰の持ち物でもない空白を見つけ、今すぐ人が動くべきときだけ呼び出しをかけます。',
    },
    'Scale & Cost': {
      name: 'スケールとコスト',
      tagline: 'キャッシュキーは主張であり、リクエストを1つずつ送るのは負荷ではなく、請求額は誰かがやらなければならない算数です。',
    },
    'Legacy': {
      name: 'レガシー',
      tagline: '変える前に記述し、間違いも固定し、まず継ぎ目を入れ、気が変わることのないもので段階的に切り替えます。',
    },
    'Configuration': {
      name: '設定管理',
      tagline: 'どこでも同じ成果物、自分がどこにいるかは環境が教え、再現できるビルドだけが、何が動いているかを知る唯一の方法です。',
    },
    'The Craft': {
      name: '仕事の流儀',
      tagline: '変更する理由が違うところで切り分け、ループを3回書き、単位を名前に入れ、負債はそれが実際にかけたコストで順位付けします。',
    },
    'The Engineer': {
      name: 'エンジニア',
      tagline: 'ログから見積もり、依頼の裏にある問いを見つけ、次の人が見つける場所に答えを残し、本当の問題を直すことで「ノー」と言います。',
    },
    'The Whole Job': {
      name: '仕事まるごと',
      tagline: '理解する前に緩和し、あとで解除し、動いていることを確認できる手段を付けて送り出し、自分がいなくても大丈夫なシステムを残します。',
    },
    Checkpoints: {
      name: 'チェックポイント',
      tagline: '章ごとに1つの課題。ヒントも手順の解説もありません。その章が、準備です。',
    },
    'Trust the input': {
      name: '入力を信じてはいけない',
      tagline: '外から来るものはすべて主張です。何がうまくいかない可能性があるかを名前で挙げ、値が文になってしまうのを防ぎます。',
    },
    'Who you are, and who says so': {
      name: 'あなたは誰で、誰がそう言うのか',
      tagline: 'アイデンティティは1つではなく2つの問いです。これは誰か、そしてその人に許されているか。セッション、URLの中のID、ログインフォームが漏らすもの。',
    },
    'Blast radius': {
      name: '影響範囲',
      tagline: '漏れるものと考えます。トークンのスコープを絞り、あらゆるクエリでテナントを明示し、誰がそれをしたのか言えるようにします。',
    },
    'Working securely with other people': {
      name: '他の人と安全に働く',
      tagline: '差分を、それが誰かに何を許すかという観点で読み、依存関係、請求書、見知らぬ人のメールアドレスもシステムの一部として数えます。',
    },
    "The agent's pull request": {
      name: 'エージェントのプルリクエスト',
      tagline: 'グリーンで、自信満々で、間違っている。主張のひとつひとつを差分と照らして確認し、そのミスが二度とマージされないようにします。',
    },
    'Calling the model': {
      name: 'モデルを呼び出す',
      tagline: '200は答えではありません。形式を検証し、なぜ止まったかを読み、プレフィックスには1回だけ支払い、信じる前に計測します。',
    },
    'Giving it tools': {
      name: 'ツールを与える',
      tagline: 'ループを回し、権限はコードの側に持たせ、読み込んだものはデータとして扱い、請求額が膨らむ前に止めます。',
    },
    'Answers from our own documents': {
      name: '自社ドキュメントからの回答',
      tagline: '適切なページを取得し、出典を示すか「わかりません」と言い、モデルを固定し、それでも間違ったときはインシデントとして自分で引き受けます。',
    },
  },
};
